import * as msgpack from 'msgpack-lite';
import * as nacl from 'tweetnacl';
import { arraysAreEqual, base64ToU8a, bufferToUrl, copyDeepOrReference, copyShallow, hasValue, hexToU8a, msgpackVisualizer, randomString, stringToUtf8a, u8aToHex, } from '../helpers';
import { isContactReceiver, isDistributionListReceiver, isGroupReceiver, isValidReceiverType, } from '../typeguards';
import { PeerConnectionHelper } from './peerconnection';
import { PushSession } from './push';
import { PushError, TimeoutError } from '../exceptions';
import { ConfidentialWireMessage } from '../helpers/confidential';
import { UnboundedFlowControlledDataChannel } from '../helpers/data_channel';
import { DeviceUnreachableController, PushRejectedDialogController } from '../partials/messenger';
import { ChunkCache } from '../protocol/cache';
import { SequenceNumber } from '../protocol/sequence_number';
const fakeConnectionId = Uint8Array.from([
    1, 2, 3, 4, 5, 6, 7, 8,
    1, 2, 3, 4, 5, 6, 7, 8,
    1, 2, 3, 4, 5, 6, 7, 8,
    1, 2, 3, 4, 5, 6, 7, 8,
]);
export class WebClientService {
    static MAX_CONNECT_ATTEMPTS = 3;
    static DATA_CHANNEL_MAX_CHUNK_SIZE = 256 * 1024;
    static RELAYED_DATA_CHUNK_SIZE = 64 * 1024;
    static SEQUENCE_NUMBER_MIN = 0;
    static SEQUENCE_NUMBER_MAX = (2 ** 32) - 1;
    static CHUNK_CACHE_SIZE_MAX = 2 * 1024 * 1024;
    static AVATAR_LOW_MAX_SIZE = 48;
    static MAX_FILE_SIZE_WEBRTC_TASK_V0 = 15 * 1024 * 1024;
    static CONNECTION_ID_NONCE = stringToUtf8a('connectionidconnectionid');
    static TYPE_REQUEST = 'request';
    static TYPE_RESPONSE = 'response';
    static TYPE_UPDATE = 'update';
    static TYPE_CREATE = 'create';
    static TYPE_DELETE = 'delete';
    static SUB_TYPE_RECEIVER = 'receiver';
    static SUB_TYPE_RECEIVERS = 'receivers';
    static SUB_TYPE_CONVERSATIONS = 'conversations';
    static SUB_TYPE_CONVERSATION = 'conversation';
    static SUB_TYPE_MESSAGE = 'message';
    static SUB_TYPE_MESSAGES = 'messages';
    static SUB_TYPE_TEXT_MESSAGE = 'textMessage';
    static SUB_TYPE_FILE_MESSAGE = 'fileMessage';
    static SUB_TYPE_AVATAR = 'avatar';
    static SUB_TYPE_THUMBNAIL = 'thumbnail';
    static SUB_TYPE_BLOB = 'blob';
    static SUB_TYPE_TYPING = 'typing';
    static SUB_TYPE_READ = 'read';
    static SUB_TYPE_CLIENT_INFO = 'clientInfo';
    static SUB_TYPE_KEY_PERSISTED = 'keyPersisted';
    static SUB_TYPE_ACK = 'ack';
    static SUB_TYPE_CONTACT_DETAIL = 'contactDetail';
    static SUB_TYPE_CONTACT = 'contact';
    static SUB_TYPE_GROUP = 'group';
    static SUB_TYPE_DISTRIBUTION_LIST = 'distributionList';
    static SUB_TYPE_ALERT = 'alert';
    static SUB_TYPE_GROUP_SYNC = 'groupSync';
    static SUB_TYPE_BATTERY_STATUS = 'batteryStatus';
    static SUB_TYPE_CLEAN_RECEIVER_CONVERSATION = 'cleanReceiverConversation';
    static SUB_TYPE_CONFIRM = 'confirm';
    static SUB_TYPE_CONFIRM_ACTION = 'confirmAction';
    static SUB_TYPE_PROFILE = 'profile';
    static SUB_TYPE_CONNECTION_ACK = 'connectionAck';
    static SUB_TYPE_CONNECTION_DISCONNECT = 'connectionDisconnect';
    static SUB_TYPE_CONNECTION_INFO = 'connectionInfo';
    static SUB_TYPE_ACTIVE_CONVERSATION = 'activeConversation';
    static ARGUMENT_MODE = 'mode';
    static ARGUMENT_MODE_NEW = 'new';
    static ARGUMENT_MODE_MODIFIED = 'modified';
    static ARGUMENT_MODE_REMOVED = 'removed';
    static ARGUMENT_RECEIVER_TYPE = 'type';
    static ARGUMENT_RECEIVER_ID = 'id';
    static ARGUMENT_TEMPORARY_ID = 'temporaryId';
    static ARGUMENT_REFERENCE_MSG_ID = 'refMsgId';
    static ARGUMENT_AVATAR = 'avatar';
    static ARGUMENT_AVATAR_HIGH_RESOLUTION = 'highResolution';
    static ARGUMENT_NICKNAME = 'publicNickname';
    static ARGUMENT_IS_TYPING = 'isTyping';
    static ARGUMENT_MESSAGE_ID = 'messageId';
    static ARGUMENT_HAS_MORE = 'more';
    static ARGUMENT_MESSAGE_ACKNOWLEDGED = 'acknowledged';
    static ARGUMENT_IDENTITY = 'identity';
    static ARGUMENT_SUCCESS = 'success';
    static ARGUMENT_SYSTEM_CONTACT = 'systemContact';
    static ARGUMENT_NAME = 'name';
    static ARGUMENT_MEMBERS = 'members';
    static ARGUMENT_FIRST_NAME = 'firstName';
    static ARGUMENT_LAST_NAME = 'lastName';
    static ARGUMENT_DELETE_TYPE = 'deleteType';
    static ARGUMENT_ERROR = 'error';
    static ARGUMENT_MAX_SIZE = 'maxSize';
    static ARGUMENT_USER_AGENT = 'userAgent';
    static ARGUMENT_BROWSER_NAME = 'browserName';
    static ARGUMENT_BROWSER_VERSION = 'browserVersion';
    static DELETE_GROUP_TYPE_LEAVE = 'leave';
    static DELETE_GROUP_TYPE_DELETE = 'delete';
    static DATA_FIELD_BLOB_BLOB = 'blob';
    static DATA_FIELD_BLOB_TYPE = 'type';
    static DATA_FIELD_BLOB_NAME = 'name';
    static DC_LABEL = 'THREEMA';
    $state;
    $rootScope;
    $q;
    $window;
    $translate;
    $filter;
    $timeout;
    $mdDialog;
    batteryStatusService;
    browserService;
    logService;
    messageService;
    mimeService;
    notificationService;
    pushService;
    qrCodeService;
    receiverService;
    stringService;
    themeService;
    timeoutService;
    titleService;
    versionService;
    log;
    arpLog;
    arpLogV;
    msgpackLog;
    startupPromise = null;
    startupDone = false;
    handoverDone = false;
    pendingInitializationStepRoutines = new Set();
    initialized = new Set();
    stateService;
    saltyRtcHost = null;
    saltyRtcQrHost = null;
    salty = null;
    connectionInfoFuture = null;
    relayedDataTask = null;
    secureDataChannel = null;
    secureDataChannelCrypto = null;
    secureDataChannelChunkLength = null;
    secureDataChannelMessageId = 0;
    chosenTask = "none";
    outgoingMessageSequenceNumber;
    previousConnectionId = null;
    currentConnectionId = null;
    previousIncomingChunkSequenceNumber = null;
    currentIncomingChunkSequenceNumber;
    previousChunkCache = null;
    currentChunkCache = null;
    handshakeCompleted = false;
    ackTimer = null;
    pendingAckRequest = null;
    unchunker = null;
    messages;
    conversations;
    receivers;
    alerts = [];
    pushToken = null;
    pushTokenType = null;
    pushSession = null;
    pushSessionConfig;
    pushSessionExpectedPeriodMaxMs;
    pushPromise = null;
    deviceUnreachableDialog = null;
    pushTimer = null;
    schedulePushAfterCooldown = false;
    batteryStatusTimeout = null;
    config;
    userConfig;
    container;
    typingInstance;
    drafts;
    pcHelper = null;
    trustedKeyStore;
    clientInfo = null;
    version = null;
    blobCache = new Map();
    loadingMessages = new Map();
    receiverListener = [];
    msgpackEncoderOptions = {
        codec: msgpack.createCodec({ binarraybuffer: true }),
    };
    msgpackDecoderOptions = {
        codec: msgpack.createCodec({ binarraybuffer: true }),
    };
    wireMessageFutures = new Map();
    static $inject = [
        '$rootScope', '$q', '$state', '$window', '$translate', '$filter', '$timeout', '$mdDialog',
        'LogService', 'Container', 'TrustedKeyStore',
        'StateService', 'NotificationService', 'MessageService', 'PushService', 'BrowserService',
        'TitleService', 'QrCodeService', 'MimeService', 'ReceiverService', 'StringService',
        'VersionService', 'BatteryStatusService', 'ThemeService', 'TimeoutService',
        'CONFIG',
    ];
    constructor($rootScope, $q, $state, $window, $translate, $filter, $timeout, $mdDialog, logService, container, trustedKeyStore, stateService, notificationService, messageService, pushService, browserService, titleService, qrCodeService, mimeService, receiverService, stringService, versionService, batteryStatusService, themeService, timeoutService, CONFIG) {
        this.$rootScope = $rootScope;
        this.$q = $q;
        this.$state = $state;
        this.$window = $window;
        this.$translate = $translate;
        this.$filter = $filter;
        this.$timeout = $timeout;
        this.$mdDialog = $mdDialog;
        this.batteryStatusService = batteryStatusService;
        this.browserService = browserService;
        this.logService = logService;
        this.messageService = messageService;
        this.mimeService = mimeService;
        this.notificationService = notificationService;
        this.pushService = pushService;
        this.qrCodeService = qrCodeService;
        this.receiverService = receiverService;
        this.stringService = stringService;
        this.themeService = themeService;
        this.timeoutService = timeoutService;
        this.titleService = titleService;
        this.versionService = versionService;
        this.config = CONFIG;
        this.userConfig = window['UserConfig'];
        this.log = logService.getLogger('WebClient-S', 'color: #fff; background-color: #0066cc');
        this.arpLog = logService.getLogger('AppRemoteProtocol', 'color: #fff; background-color: #0099cc', this.userConfig.ARP_LOG_LEVEL);
        this.arpLogV = logService.getLogger('AppRemoteProtocol', 'color: #fff; background-color: #00ace6', this.userConfig.ARP_LOG_TRACE ? 'debug' : 'none');
        this.msgpackLog = logService.getLogger('MessagePack', 'color: #fff; background-color: #006699', this.userConfig.MSGPACK_LOG_TRACE ? 'debug' : 'none');
        this.stateService = stateService;
        this.pushSessionConfig = PushSession.defaultConfig;
        this.pushSessionConfig.triesMax = WebClientService.MAX_CONNECT_ATTEMPTS;
        this.pushSessionConfig = {
            retryTimeoutInitMs: 14000, retryTimeoutMaxMs: 30000, triesMax: 1, timeToLiveRange: [90],
        };
        this.pushSessionExpectedPeriodMaxMs = PushSession.expectedPeriodMaxMs(this.pushSessionConfig);
        this.container = container;
        this.trustedKeyStore = trustedKeyStore;
        this.drafts = this.container.createDrafts();
        this._resetFields();
        this.stateService.evtConnectionBuildupStateChange.attach((stateChange) => {
            if (this.startupPromise !== null) {
                this.startupPromise.notify(stateChange);
            }
        });
        this.stateService.evtGlobalConnectionStateChange.attach(this.handleGlobalConnectionStateChange.bind(this));
    }
    get readyToSubmit() {
        return this.stateService.readyToSubmit(this.chosenTask, this.startupDone);
    }
    get me() {
        return this.receivers.me;
    }
    get contacts() {
        return this.receivers.contacts;
    }
    get groups() {
        return this.receivers.groups;
    }
    get distributionLists() {
        return this.receivers.distributionLists;
    }
    get typing() {
        return this.typingInstance;
    }
    get unacknowledgedWireMessages() {
        return this.wireMessageFutures.size;
    }
    get requiresTemporaryIdBackwardsCompatibility() {
        return this.chosenTask !== "relayed-data";
    }
    buildQrCodePayload(persistent) {
        return this.qrCodeService.buildQrCodePayload(this.salty.permanentKeyBytes, this.salty.authTokenBytes, hexToU8a(this.userConfig.SALTYRTC_SERVER_KEY), this.saltyRtcQrHost, hasValue(this.userConfig.SALTYRTC_QR_PORT) ? this.userConfig.SALTYRTC_QR_PORT : this.userConfig.SALTYRTC_PORT, persistent);
    }
    init(flags) {
        let keyStore = flags.keyStore;
        let resumeSession = flags.resume;
        this.log.info(`Initializing (keyStore=${keyStore !== undefined ? 'yes' : 'no'}, peerTrustedKey=` +
            `${flags.peerTrustedKey !== undefined ? 'yes' : 'no'}, resume=${resumeSession})`);
        if (!resumeSession) {
            this.clearCache();
            this.wireMessageFutures.clear();
            this.timeoutService.cancelAll();
        }
        if (resumeSession) {
            if (this.previousConnectionId) {
                this.arpLog.debug(`Trying to resume previous session (id=${u8aToHex(this.previousConnectionId)}, ` +
                    `sn-out=${this.previousChunkCache.sequenceNumber.get()})`);
            }
            else {
                resumeSession = false;
                this.arpLog.debug('Wanted to resume previous session but none exists');
            }
        }
        else {
            this.discardSession({ resetMessageSequenceNumber: true });
            resumeSession = false;
            this.arpLog.debug('Discarded previous session');
        }
        this.handshakeCompleted = false;
        this.currentConnectionId = null;
        this.currentIncomingChunkSequenceNumber = new SequenceNumber(0, WebClientService.SEQUENCE_NUMBER_MIN, WebClientService.SEQUENCE_NUMBER_MAX);
        const outgoingChunkSequenceNumber = new SequenceNumber(0, WebClientService.SEQUENCE_NUMBER_MIN, WebClientService.SEQUENCE_NUMBER_MAX);
        this.currentChunkCache = new ChunkCache(outgoingChunkSequenceNumber);
        this.pendingAckRequest = null;
        this.connectionInfoFuture = new Future();
        const tasks = [];
        if (this.browserService.supportsWebrtcTask()) {
            tasks.push(new saltyrtcTaskWebrtc.WebRTCTaskBuilder()
                .withLoggingLevel(this.userConfig.SALTYRTC_LOG_LEVEL)
                .withVersion('v1')
                .withHandover(true)
                .build());
            tasks.push(new saltyrtcTaskWebrtc.WebRTCTaskBuilder()
                .withLoggingLevel(this.userConfig.SALTYRTC_LOG_LEVEL)
                .withVersion('v0')
                .withHandover(true)
                .withMaxChunkLength(this.browserService.getBrowser().isFirefox(false) ? 16384 : 65536)
                .build());
        }
        this.relayedDataTask = new saltyrtcTaskRelayedData.RelayedDataTask(this.userConfig.SALTYRTC_LOG_LEVEL === 'debug');
        tasks.push(this.relayedDataTask);
        if (!keyStore) {
            keyStore = new saltyrtcClient.KeyStore();
        }
        this.saltyRtcHost = this.userConfig.SALTYRTC_HOST.replace('{prefix}', keyStore.publicKeyHex.substr(0, 2));
        this.saltyRtcQrHost = this.saltyRtcHost;
        if (hasValue(this.userConfig.SALTYRTC_QR_HOST)) {
            this.saltyRtcQrHost = this.userConfig.SALTYRTC_QR_HOST.replace('{prefix}', keyStore.publicKeyHex.substr(0, 2));
        }
        let builder = new saltyrtcClient.SaltyRTCBuilder()
            .connectTo(this.saltyRtcHost, this.userConfig.SALTYRTC_PORT)
            .withLoggingLevel(this.userConfig.SALTYRTC_LOG_LEVEL)
            .withServerKey(this.userConfig.SALTYRTC_SERVER_KEY)
            .withKeyStore(keyStore)
            .usingTasks(tasks)
            .withPingInterval(30);
        if (flags.peerTrustedKey !== undefined) {
            builder = builder.withTrustedPeerKey(flags.peerTrustedKey);
        }
        this.salty = builder.asInitiator();
        this.arpLog.info('Public key:', this.salty.permanentKeyHex);
        this.arpLogV.debug('Auth token:', this.salty.authTokenHex);
        this.salty.on('state-change', (ev) => {
            const state = ev.data;
            if (!this.startupDone) {
                switch (state) {
                    case 'new':
                    case 'ws-connecting':
                    case 'server-handshake':
                        if (this.stateService.connectionBuildupState !== 'push'
                            && this.stateService.connectionBuildupState !== 'manual_start') {
                            this.stateService.updateConnectionBuildupState('connecting');
                        }
                        break;
                    case 'peer-handshake':
                        if (this.stateService.connectionBuildupState !== 'push'
                            && this.stateService.connectionBuildupState !== 'manual_start') {
                            this.stateService.updateConnectionBuildupState('waiting');
                        }
                        break;
                    case 'task':
                        this.onTaskEstablished(resumeSession);
                        break;
                    case 'closing':
                    case 'closed':
                        this.stateService.updateConnectionBuildupState('closed');
                        break;
                    default:
                        this.arpLog.warn('Unknown signaling state:', state);
                }
            }
            this.stateService.updateSignalingConnectionState(state, this.chosenTask, this.handoverDone);
        });
        this.salty.on('peer-disconnected', (ev) => {
            this.$rootScope.$apply(() => {
                this.onPeerDisconnected(ev.data);
            });
        });
        this.salty.on('handover', () => {
            this.arpLog.debug('Handover done');
            this.onHandover(resumeSession);
        });
        this.salty.on('connection-error', (ev) => {
            this.arpLog.error('Connection error:', ev);
        });
        this.salty.on('connection-closed', (ev) => {
            this.arpLog.info('Connection closed:', ev);
        });
        this.salty.on('no-shared-task', (ev) => {
            this.arpLog.warn('No shared task found:', ev.data);
            const offeredWebrtc = ev.data.offered.filter((t) => t.endsWith('webrtc.tasks.saltyrtc.org')).length > 0;
            this.$rootScope.$apply(() => {
                if (!this.browserService.supportsWebrtcTask() && offeredWebrtc) {
                    this.failSession(false);
                    this.showWebrtcAndroidWarning();
                }
                else {
                    this.failSession();
                }
            });
        });
    }
    showWebrtcAndroidWarning() {
        this.$translate.onReady().then(() => {
            const confirm = this.$mdDialog.alert()
                .title(this.$translate.instant('welcome.BROWSER_NOT_SUPPORTED'))
                .htmlContent(this.$translate.instant('welcome.BROWSER_NOT_SUPPORTED_DETAILS', {
                firefoxLink: '<a href="https://www.mozilla.org/firefox/" target="_blank" rel="noopener noreferrer">Firefox</a>',
                chromeLink: '<a href="https://www.google.com/chrome/browser/desktop/" target="_blank" rel="noopener noreferrer">Chrome</a>',
                operaLink: '<a href="https://www.opera.com/" target="_blank" rel="noopener noreferrer">Opera</a>',
                edgeLink: '<a href="https://www.microsoft.com/edge" target="_blank" rel="noopener noreferrer">Edge</a>',
                safariLink: '<a href="https://www.apple.com/safari/" target="_blank" rel="noopener noreferrer">Safari</a>',
            }))
                .ok(this.$translate.instant('welcome.ABORT'));
            this.$mdDialog.show(confirm).then(() => {
                window.location.replace('https://threema.ch/threema-web');
            });
        });
    }
    showAlert(alertMessage) {
        this.$timeout(() => {
            this.$mdDialog.show(this.$mdDialog.alert()
                .title(this.$translate.instant('connection.SESSION_CLOSED_TITLE'))
                .textContent(this.$translate.instant(alertMessage))
                .ok(this.$translate.instant('common.OK')));
        }, 0);
    }
    failSession(showAlert = true) {
        const stop = () => {
            this.stop({
                reason: "error",
                send: true,
                close: 'welcome',
                connectionBuildupState: 'closed',
            });
            if (showAlert) {
                this.showAlert('connection.SESSION_ERROR');
            }
        };
        if (!this.$rootScope.$$phase) {
            this.$rootScope.$apply(() => stop());
        }
        else {
            stop();
        }
    }
    failSessionOnReject(type, subType) {
        return ((error) => {
            this.logOnReject(type, subType)(error);
            this.failSession();
        });
    }
    logOnReject(type, subType) {
        return ((error) => {
            this.arpLog.error(`Message ${type}/${subType} has been rejected by the remote: ${error}`);
        });
    }
    maybeResumeSession(resumeSession, remoteInfo) {
        const remoteCurrentConnectionId = new Uint8Array(remoteInfo.id);
        if (arraysAreEqual(fakeConnectionId, remoteCurrentConnectionId)) {
            this.arpLog.debug('Cannot resume session: Remote did not implement deriving the connection ID');
            return false;
        }
        if (!arraysAreEqual(this.currentConnectionId, remoteCurrentConnectionId)) {
            this.arpLog.info(`Cannot resume session: IDs of previous connection do not match (local=`
                + `${u8aToHex(this.currentConnectionId)}, remote=${u8aToHex(remoteCurrentConnectionId)}`);
            throw new Error('Derived connection IDs do not match!');
        }
        if (!resumeSession || remoteInfo.resume === undefined) {
            this.arpLog.info(`No resumption (local requested: ${resumeSession ? 'yes' : 'no'}, ` +
                `remote requested: ${remoteInfo.resume ? 'yes' : 'no'})`);
            return false;
        }
        const remotePreviousConnectionId = new Uint8Array(remoteInfo.resume.id);
        if (!arraysAreEqual(this.previousConnectionId, remotePreviousConnectionId)) {
            this.arpLog.info(`Cannot resume session: IDs of previous connection do not match (local=`
                + `${u8aToHex(this.previousConnectionId)}, remote=${u8aToHex(remotePreviousConnectionId)}`);
            return false;
        }
        const size = this.previousChunkCache.byteLength;
        let result;
        this.arpLog.debug(`Pruning cache (local-sn=${this.previousChunkCache.sequenceNumber.get()}, ` +
            `remote-sn=${remoteInfo.resume.sequenceNumber})`);
        try {
            result = this.previousChunkCache.prune(remoteInfo.resume.sequenceNumber);
        }
        catch (error) {
            throw new Error(`Unable to resume session: ${error}`);
        }
        this.arpLog.debug(`Chunk cache pruned, acknowledged: ${result.acknowledged}, left: ${result.left}, size: ` +
            `${size} -> ${this.previousChunkCache.byteLength}`);
        if (this.userConfig.ARP_LOG_TRACE) {
            this.arpLog.debug(`Chunks that require acknowledgement: ${this.previousChunkCache.chunks.length}`);
        }
        const transferred = this.currentChunkCache.transfer(this.previousChunkCache.chunks);
        this.arpLog.debug(`Chunk cache transferred (${transferred} chunks)`);
        this.previousConnectionId = null;
        this.previousIncomingChunkSequenceNumber = null;
        this.previousChunkCache = null;
        const chunks = this.currentChunkCache.chunks;
        this.arpLog.debug(`Sending cached chunks: ${chunks.length}`);
        for (const chunk of chunks) {
            this.sendChunk(chunk, true, false, false);
        }
        return true;
    }
    discardSession(flags) {
        if (flags.resetMessageSequenceNumber) {
            this.outgoingMessageSequenceNumber = new SequenceNumber(0, WebClientService.SEQUENCE_NUMBER_MIN, WebClientService.SEQUENCE_NUMBER_MAX);
        }
        this.unchunker = new chunkedDc.UnreliableUnorderedUnchunker();
        this.unchunker.onMessage = this.handleIncomingMessageBytes.bind(this);
        this.previousConnectionId = null;
        this.previousIncomingChunkSequenceNumber = null;
        this.previousChunkCache = null;
    }
    scheduleConnectionAck(timeout = 10000) {
        if (this.ackTimer === null) {
            this.ackTimer = self.setTimeout(() => {
                this.ackTimer = null;
                this._sendConnectionAck();
            }, timeout);
        }
    }
    schedulePush(timeoutMs = 3000) {
        if (this.pushTimer !== null) {
            this.schedulePushAfterCooldown = true;
            return;
        }
        this.pushTimer = self.setTimeout(() => {
            this.pushTimer = null;
            this.schedulePushAfterCooldown = false;
            this.log.debug('Connection appears to be lost, sending push');
            this.sendPush();
        }, timeoutMs);
        this._requestConnectionAck();
    }
    cancelPush(cooldownMs = 10000) {
        if (this.pushTimer !== null) {
            self.clearTimeout(this.pushTimer);
            this.pushTimer = null;
        }
        this.schedulePushAfterCooldown = false;
        if (cooldownMs > 0) {
            this.pushTimer = self.setTimeout(() => {
                this.pushTimer = null;
                if (this.schedulePushAfterCooldown) {
                    this.schedulePush();
                }
            }, cooldownMs);
        }
    }
    onTaskEstablished(resumeSession) {
        this.resetPushSession(true);
        this.stateService.updateConnectionBuildupState('peer_handshake');
        const task = this.salty.getTask();
        if (task.getName().indexOf('webrtc.tasks.saltyrtc.org') !== -1) {
            this.chosenTask = "webrtc";
        }
        else if (task.getName().indexOf('relayed-data.tasks.saltyrtc.org') !== -1) {
            this.chosenTask = "relayed-data";
        }
        else {
            throw new Error('Invalid or unknown task name: ' + task.getName());
        }
        if (this.chosenTask === "webrtc") {
            const browser = this.browserService.getBrowser();
            const prefix = u8aToHex(nacl.randomBytes(1));
            const iceServers = this.userConfig.ICE_SERVERS.map((server) => {
                server = copyShallow(server);
                const urls = Array.isArray(server.urls) ? server.urls : [server.urls];
                server.urls = urls.map((url) => url.replace('{prefix}', prefix));
                return server;
            });
            this.pcHelper = new PeerConnectionHelper(this.$q, this.$rootScope, this.userConfig, this.logService, this.timeoutService, task, iceServers);
            this.pcHelper.onConnectionStateChange = (state) => {
                this.stateService.updateTaskConnectionState(state);
            };
            this.pcHelper.handover();
        }
        else {
            this.onHandover(resumeSession);
        }
    }
    async onConnectionEstablished(resumeSession) {
        if (resumeSession) {
            const incomingSequenceNumber = this.previousIncomingChunkSequenceNumber.get();
            this.arpLog.debug(`Sending connection info (resume=yes, sn-in=${incomingSequenceNumber})`);
            this._sendConnectionInfo(this.currentConnectionId.buffer, this.previousConnectionId.buffer, incomingSequenceNumber);
        }
        else {
            this.arpLog.debug('Sending connection info (resume=no)');
            this._sendConnectionInfo(this.currentConnectionId.buffer);
        }
        let remoteInfo;
        try {
            remoteInfo = await this.connectionInfoFuture;
        }
        catch (error) {
            this.arpLog.error(error);
            this.failSession();
            return;
        }
        let outgoingSequenceNumber = 'n/a';
        let remoteResume = 'no';
        if (remoteInfo.resume !== undefined) {
            outgoingSequenceNumber = remoteInfo.resume.sequenceNumber;
            remoteResume = 'yes';
        }
        this.arpLog.debug(`Received connection info (resume=${remoteResume}, ` +
            `sn-out=${outgoingSequenceNumber})`);
        let sessionWasResumed;
        try {
            sessionWasResumed = this.maybeResumeSession(resumeSession, remoteInfo);
        }
        catch (error) {
            this.arpLog.error(error);
            this.failSession();
            return;
        }
        this.handshakeCompleted = true;
        const requiredInitializationSteps = [];
        if (!resumeSession || !sessionWasResumed) {
            this.discardSession({ resetMessageSequenceNumber: false });
            this.arpLog.debug('Session discarded');
            this.clearCache();
            this.wireMessageFutures.clear();
            this.timeoutService.cancelAll();
            requiredInitializationSteps.push("client info", "conversations", "receivers", "profile");
            this._requestInitialData();
        }
        else {
            this.arpLog.debug('Session resumed');
        }
        if (this.startupPromise !== null) {
            this.runAfterInitializationSteps(requiredInitializationSteps, () => {
                this.stateService.updateConnectionBuildupState('done');
                this.startupPromise.resolve();
                this.startupPromise = null;
                this.startupDone = true;
                this._resetInitializationSteps();
                if (this.chosenTask !== "relayed-data" && this.$state.includes('messenger')) {
                    this.$state.reload().catch((error) => {
                        this.log.error('Unable to reload state:', error);
                    });
                }
            });
        }
        if (resumeSession && !sessionWasResumed && this.clientInfo !== null) {
            this.$rootScope.$apply(() => {
                if (this.chosenTask !== "relayed-data") {
                    return;
                }
                if (this.$state.includes('messenger')) {
                    this.$state.go('messenger.home');
                }
            });
        }
        this.timeoutService.register(() => this.versionService.checkForUpdate(), 7000, true, 'checkForUpdate');
        this.stateService.updateConnectionBuildupState('loading');
    }
    onHandover(resumeSession) {
        this.log.debug('Initializing NotificationService...');
        this.notificationService.init();
        const connectionIdBox = this.salty.encryptForPeer(new Uint8Array(0), WebClientService.CONNECTION_ID_NONCE);
        this.currentConnectionId = new Uint8Array(connectionIdBox.data);
        if (this.chosenTask === "webrtc") {
            const task = this.salty.getTask();
            this.arpLog.debug(`Creating data channel ${WebClientService.DC_LABEL}`);
            const pc = this.pcHelper.pc;
            const dc = pc.createDataChannel(WebClientService.DC_LABEL);
            dc.binaryType = 'arraybuffer';
            this.secureDataChannel = new UnboundedFlowControlledDataChannel(dc, this.logService, this.userConfig.TRANSPORT_LOG_LEVEL);
            this.secureDataChannelCrypto = task.createCryptoContext(dc.id);
            const unchunker = new chunkedDc.UnreliableUnorderedUnchunker();
            dc.onopen = () => {
                this.arpLog.info(`Data channel ${dc.label} open`);
                this.secureDataChannelChunkLength = Math.min(WebClientService.DATA_CHANNEL_MAX_CHUNK_SIZE, pc.sctp.maxMessageSize);
                this.arpLog.debug(`Using chunk length: ${this.secureDataChannelChunkLength} for data channel ${dc.label}`);
                this.onConnectionEstablished(resumeSession).catch((error) => {
                    this.arpLog.error('Error during handshake:', error);
                });
            };
            dc.onclose = () => {
                this.arpLog.warn(`Data channel ${dc.label} closed (ICE state: ${pc.iceConnectionState})`);
            };
            dc.onerror = (event) => {
                this.arpLog.warn(`Data channel ${dc.label} error (closed=${dc.onclose === null}):`, event.error);
            };
            dc.onmessage = (event) => {
                this.arpLogV.debug(`Data channel ${dc.label} incoming chunk of length ${event.data.byteLength}`);
                unchunker.add(new Uint8Array(event.data));
            };
            unchunker.onMessage = (array) => {
                const box = saltyrtcClient.Box.fromUint8Array(array, saltyrtcTaskWebrtc.DataChannelCryptoContext.NONCE_LENGTH);
                const message = this.secureDataChannelCrypto.decrypt(box);
                this.handleIncomingMessageBytes(message);
            };
            this.handoverDone = true;
        }
        else if (this.chosenTask === "relayed-data") {
            this.relayedDataTask.on('data', (ev) => {
                this.receiveChunk(new Uint8Array(ev.data));
            });
            this.onConnectionEstablished(resumeSession).catch((error) => {
                this.arpLog.error('Error during handshake:', error);
            });
        }
    }
    onPeerDisconnected(peerId) {
        switch (this.chosenTask) {
            case "relayed-data":
                if (this.stateService.taskConnectionState === "connected") {
                    this.stateService.updateTaskConnectionState("reconnecting");
                }
                else {
                    this.arpLog.debug('Ignoring peer-disconnected event (state is '
                        + this.stateService.taskConnectionState + ')');
                }
                break;
            default:
                this.arpLog.debug('Ignoring peer-disconnected event (chosen task is ' + this.chosenTask + ')');
        }
    }
    sendPush() {
        if (this.pushSession === null) {
            this.pushSession = this.pushService.createSession(this.salty.permanentKeyBytes, this.pushSessionConfig);
            this.pushPromise = this.pushSession.start()
                .then(() => this.resetPushSession(true))
                .catch((error) => {
                this.resetPushSession(false);
                if (error instanceof TimeoutError) {
                    this.showDeviceUnreachableDialog();
                }
                else if (error instanceof PushError && error.statusCode === 400) {
                    this.showPushRejectedDialog();
                }
                else {
                    this.failSession();
                }
            });
            if (!this.$rootScope.$$phase) {
                this.$rootScope.$apply(() => this.stateService.updateConnectionBuildupState('push'));
            }
            else {
                this.stateService.updateConnectionBuildupState('push');
            }
        }
        return [this.pushSessionExpectedPeriodMaxMs, this.pushPromise];
    }
    resetPushSession(hideDeviceUnreachableDialog = true) {
        if (hideDeviceUnreachableDialog && this.deviceUnreachableDialog !== null) {
            this.$mdDialog.hide();
        }
        if (this.pushSession !== null) {
            this.pushSession.done();
            this.pushSession = null;
            this.pushPromise = null;
        }
    }
    showDeviceUnreachableDialog() {
        if (this.pushService.isAvailable()
            && this.$state.includes('messenger')
            && this.deviceUnreachableDialog === null) {
            this.deviceUnreachableDialog = this.$mdDialog.show({
                controller: DeviceUnreachableController,
                controllerAs: 'ctrl',
                templateUrl: 'partials/dialog.device-unreachable.html',
                parent: angular.element(document.body),
                escapeToClose: false,
                onRemoving: () => { this.deviceUnreachableDialog = null; },
            });
        }
    }
    showPushRejectedDialog() {
        this.$mdDialog.show({
            controller: PushRejectedDialogController,
            controllerAs: 'ctrl',
            templateUrl: 'partials/dialog.push-rejected.html',
            parent: angular.element(document.body),
            escapeToClose: false,
        });
    }
    start(skipPush = false) {
        this.log.debug('Starting WebClientService...');
        if (this.startupPromise !== null) {
            this.log.debug('Reusing startup promise (was not resolved)');
        }
        else {
            this.log.debug('Creating new startup promise');
            this.startupPromise = this.$q.defer();
        }
        this.startupDone = false;
        this.handoverDone = false;
        this.salty.connect();
        if (this.pushService.isAvailable()) {
            if (skipPush === true) {
                this.log.debug('start(): Skipping push notification');
            }
            else {
                this.sendPush();
            }
        }
        else if (this.trustedKeyStore.hasTrustedKey()) {
            this.log.debug('Push service not available');
            this.stateService.updateConnectionBuildupState('manual_start');
        }
        return this.startupPromise.promise;
    }
    stop(args) {
        if (args.close === true) {
            throw new Error('args.close has been set to "true" but requires a redirect state instead');
        }
        this.log.info(`Stopping (reason=${args.reason}, send=${args.send}, close=${args.close}, ` +
            'connectionBuildupState=' +
            `${args.connectionBuildupState !== undefined ? args.connectionBuildupState : 'n/a'})`);
        let close = args.close !== false;
        let remove = false;
        this.resetPushSession(true);
        if (args.reason === "delete") {
            close = true;
            remove = true;
        }
        if (args.reason === "replace" || args.reason === "oom" ||
            args.reason === "error") {
            close = true;
        }
        if (args.send && this.stateService.state === "ok") {
            this.sendUpdateWireMessage(WebClientService.SUB_TYPE_CONNECTION_DISCONNECT, false, undefined, { reason: args.reason });
        }
        if (this.ackTimer !== null) {
            self.clearTimeout(this.ackTimer);
            this.ackTimer = null;
        }
        if (this.pushTimer !== null) {
            this.cancelPush(0);
        }
        this.log.debug('Timer stopped');
        const shouldResetUnreadCount = this.chosenTask !== "relayed-data" || close;
        this.stateService.reset(args.connectionBuildupState, shouldResetUnreadCount);
        if (remove) {
            this.trustedKeyStore.clearTrustedKey();
        }
        if (close) {
            this.previousConnectionId = null;
            this.previousIncomingChunkSequenceNumber = null;
            this.previousChunkCache = null;
            this.chosenTask = "none";
            this.clientInfo = null;
            this.clearCache();
            this.wireMessageFutures.clear();
            this.timeoutService.cancelAll();
            this.pushService.reset();
            this.arpLog.debug('Session closed (cannot be resumed)');
        }
        else {
            if (this.handshakeCompleted) {
                this.previousConnectionId = this.currentConnectionId;
                this.previousIncomingChunkSequenceNumber = this.currentIncomingChunkSequenceNumber;
                this.previousChunkCache = this.currentChunkCache;
            }
            this.arpLog.debug('Session remains open');
        }
        if (this.secureDataChannel !== null) {
            this.arpLog.debug('Closing data channel');
            this.secureDataChannel.dc.onopen = null;
            this.secureDataChannel.dc.onmessage = null;
            this.secureDataChannel.dc.onbufferedamountlow = null;
            this.secureDataChannel.dc.onerror = null;
            this.secureDataChannel.dc.onclose = null;
            this.secureDataChannel.dc.close();
        }
        if (this.relayedDataTask !== null) {
            this.relayedDataTask.off();
        }
        if (this.salty !== null) {
            this.arpLog.debug('Closing signaling');
            this.salty.off();
            this.salty.disconnect(true);
        }
        if (this.pcHelper !== null) {
            this.pcHelper.onConnectionStateChange = null;
            this.pcHelper.close();
            this.arpLog.debug('Peer connection closed');
        }
        else {
            this.arpLog.debug('Peer connection was null');
        }
        if (close) {
            if (this.startupPromise !== null) {
                this.startupPromise.reject();
                this.startupPromise = null;
                this._resetInitializationSteps();
            }
            const state = args.close !== false ? args.close : 'welcome';
            this.$state.go(state);
        }
    }
    registerInitializationStep(name) {
        if (this.initialized.has(name)) {
            this.arpLog.warn('Initialization step "' + name + '" already registered');
            return;
        }
        this.arpLog.debug('Initialization step "' + name + '" done');
        this.initialized.add(name);
        for (const routine of this.pendingInitializationStepRoutines) {
            const ready = routine.requiredSteps.every((requiredStep) => {
                return this.initialized.has(requiredStep);
            });
            if (ready) {
                this.arpLog.debug('Running routine after initialization "' + name + '" completed');
                this.pendingInitializationStepRoutines.delete(routine);
                routine.callback.apply(this);
            }
        }
    }
    setReceiverListener(listener) {
        this.receiverListener.push(listener);
    }
    _sendConnectionInfo(connectionId, resumeId, sequenceNumber) {
        const data = { id: connectionId };
        if (resumeId !== undefined && sequenceNumber !== undefined) {
            data.resume = {
                id: resumeId,
                sequenceNumber: sequenceNumber,
            };
        }
        this.sendUpdateWireMessage(WebClientService.SUB_TYPE_CONNECTION_INFO, false, undefined, data);
    }
    _requestConnectionAck() {
        this.sendRequestWireMessage(WebClientService.SUB_TYPE_CONNECTION_ACK, false);
    }
    _sendConnectionAck() {
        this.sendUpdateWireMessage(WebClientService.SUB_TYPE_CONNECTION_ACK, false, undefined, {
            sequenceNumber: this.currentIncomingChunkSequenceNumber.get(),
        });
        if (this.ackTimer !== null) {
            self.clearTimeout(this.ackTimer);
            this.ackTimer = null;
        }
    }
    requestClientInfo() {
        this.arpLog.debug('Sending client info request');
        const browser = this.browserService.getBrowser();
        const data = {
            [WebClientService.ARGUMENT_USER_AGENT]: navigator.userAgent,
        };
        if (hasValue(browser.name)) {
            data[WebClientService.ARGUMENT_BROWSER_NAME] = browser.name;
        }
        if (hasValue(browser.version)) {
            data[WebClientService.ARGUMENT_BROWSER_VERSION] = browser.version;
        }
        const subType = WebClientService.SUB_TYPE_CLIENT_INFO;
        this.sendRequestWireMessage(subType, !this.requiresTemporaryIdBackwardsCompatibility, undefined, data)
            .catch(this.failSessionOnReject(WebClientService.TYPE_REQUEST, subType));
    }
    requestReceivers() {
        this.arpLog.debug('Sending receivers request');
        const subType = WebClientService.SUB_TYPE_RECEIVERS;
        this.sendRequestWireMessage(subType, !this.requiresTemporaryIdBackwardsCompatibility)
            .catch(this.failSessionOnReject(WebClientService.TYPE_REQUEST, subType));
    }
    requestConversations() {
        this.arpLog.debug('Sending conversation request');
        const subType = WebClientService.SUB_TYPE_CONVERSATIONS;
        const args = { [WebClientService.ARGUMENT_MAX_SIZE]: WebClientService.AVATAR_LOW_MAX_SIZE };
        this.sendRequestWireMessage(subType, !this.requiresTemporaryIdBackwardsCompatibility, args)
            .catch(this.failSessionOnReject(WebClientService.TYPE_REQUEST, subType));
    }
    requestBatteryStatus() {
        this.arpLog.debug('Sending battery status request');
        const subType = WebClientService.SUB_TYPE_BATTERY_STATUS;
        this.sendRequestWireMessage(subType, !this.requiresTemporaryIdBackwardsCompatibility)
            .catch(this.failSessionOnReject(WebClientService.TYPE_REQUEST, subType));
    }
    requestProfile() {
        this.arpLog.debug('Sending profile request');
        const subType = WebClientService.SUB_TYPE_PROFILE;
        this.sendRequestWireMessage(subType, !this.requiresTemporaryIdBackwardsCompatibility)
            .catch(this.failSessionOnReject(WebClientService.TYPE_REQUEST, subType));
    }
    requestMessages(receiver) {
        this.arpLog.debug('requestMessages');
        if (!this.messages.hasMore(receiver)) {
            this.messages.notify(receiver, this.$rootScope);
            this.arpLog.debug('requestMessages: No more messages available');
            return null;
        }
        this.loadingMessages.set(receiver.type + receiver.id, true);
        if (this.messages.isRequested(receiver)) {
            this.arpLog.debug('requestMessages: Already requested');
            return null;
        }
        const refMsgId = this.messages.getReferenceMsgId(receiver);
        this.messages.setRequested(receiver);
        const args = {
            [WebClientService.ARGUMENT_RECEIVER_TYPE]: receiver.type,
            [WebClientService.ARGUMENT_RECEIVER_ID]: receiver.id,
        };
        const msgId = this.messages.getReferenceMsgId(receiver);
        if (msgId !== null) {
            args[WebClientService.ARGUMENT_REFERENCE_MSG_ID] = msgId;
        }
        this.arpLog.debug('Sending message request for', receiver.type, receiver.id, 'with message id', msgId);
        const subType = WebClientService.SUB_TYPE_MESSAGES;
        this.sendRequestWireMessage(subType, !this.requiresTemporaryIdBackwardsCompatibility, args)
            .catch(this.logOnReject(WebClientService.TYPE_REQUEST, subType));
        return refMsgId;
    }
    requestAvatar(receiver, highResolution) {
        const resolution = highResolution ? 'high' : 'low';
        const receiverInfo = this.receivers.getData(receiver);
        if (receiverInfo && receiverInfo.avatar && receiverInfo.avatar[resolution]) {
            return Promise.resolve(receiverInfo.avatar[resolution]);
        }
        let receiverType = receiver.type;
        if (receiverType === 'me') {
            receiverType = 'contact';
        }
        const args = {
            [WebClientService.ARGUMENT_RECEIVER_TYPE]: receiverType,
            [WebClientService.ARGUMENT_RECEIVER_ID]: receiver.id,
            [WebClientService.ARGUMENT_AVATAR_HIGH_RESOLUTION]: highResolution,
        };
        if (!highResolution) {
            args[WebClientService.ARGUMENT_MAX_SIZE] = WebClientService.AVATAR_LOW_MAX_SIZE;
        }
        this.arpLog.debug('Sending', resolution, 'res avatar request for', receiver.type, receiver.id);
        const subType = WebClientService.SUB_TYPE_AVATAR;
        return this.sendRequestWireMessage(subType, true, args);
    }
    requestThumbnail(receiver, message) {
        if (message.thumbnail !== undefined && message.thumbnail.img !== undefined) {
            return new Promise((e) => {
                e(message.thumbnail.img);
            });
        }
        const args = {
            [WebClientService.ARGUMENT_MESSAGE_ID]: message.id.toString(),
            [WebClientService.ARGUMENT_RECEIVER_TYPE]: receiver.type,
            [WebClientService.ARGUMENT_RECEIVER_ID]: receiver.id,
        };
        this.arpLog.debug('Sending thumbnail request for', receiver.type, message.id);
        const subType = WebClientService.SUB_TYPE_THUMBNAIL;
        return this.sendRequestWireMessage(subType, true, args);
    }
    requestBlob(msgId, receiver) {
        const cached = this.blobCache.get(msgId + receiver.type);
        if (cached !== undefined) {
            this.arpLog.debug('Use cached blob');
            return new Promise((resolve) => {
                resolve(cached);
            });
        }
        const args = {
            [WebClientService.ARGUMENT_RECEIVER_TYPE]: receiver.type,
            [WebClientService.ARGUMENT_RECEIVER_ID]: receiver.id,
            [WebClientService.ARGUMENT_MESSAGE_ID]: msgId,
        };
        this.arpLog.debug('Sending blob request for message', msgId);
        return this.sendRequestWireMessage(WebClientService.SUB_TYPE_BLOB, true, args);
    }
    requestRead(receiver, newestMessage) {
        if (newestMessage.id === undefined) {
            this.arpLog.warn('Called requestRead on a message without id');
            return;
        }
        if (newestMessage.type === 'status') {
            this.arpLog.warn('Called requestRead on a status message');
            return;
        }
        const args = {
            [WebClientService.ARGUMENT_RECEIVER_TYPE]: receiver.type,
            [WebClientService.ARGUMENT_RECEIVER_ID]: receiver.id,
            [WebClientService.ARGUMENT_MESSAGE_ID]: newestMessage.id.toString(),
        };
        this.arpLog.debug('Sending read request for', receiver.type, receiver.id, '(msg ' + newestMessage.id + ')');
        const subType = WebClientService.SUB_TYPE_READ;
        this.sendRequestWireMessage(subType, !this.requiresTemporaryIdBackwardsCompatibility, args)
            .catch(this.logOnReject(WebClientService.TYPE_REQUEST, subType));
    }
    requestContactDetail(contactReceiver) {
        const args = {
            [WebClientService.ARGUMENT_IDENTITY]: contactReceiver.id,
        };
        return this.sendRequestWireMessage(WebClientService.SUB_TYPE_CONTACT_DETAIL, true, args);
    }
    async sendMessage(baseReceiver, sendType, data, options = {}) {
        let reflectedType;
        const receiver = this.receivers.getData(baseReceiver);
        if (isContactReceiver(receiver)) {
            if (receiver.isBlocked) {
                throw this.$translate.instant('error.CONTACT_BLOCKED');
            }
            if (receiver.state === 'INVALID') {
                throw this.$translate.instant('error.CONTACT_INVALID');
            }
        }
        let subType;
        switch (sendType) {
            case 'text':
                reflectedType = 'text';
                subType = WebClientService.SUB_TYPE_TEXT_MESSAGE;
                const textData = data;
                const msgLength = textData.text.length;
                if (msgLength === 0) {
                    this.log.warn('Ignored empty text message');
                    throw this.$translate.instant('error.ERROR_OCCURRED');
                }
                if (data.quote !== undefined && data.quote !== null) {
                    const quoteOverhead = 12
                        + (data.quote.text.match(/\n/g) || []).length * 2
                        + 16;
                    const textByteLength = (new TextEncoder().encode(textData.text)).length;
                    const quoteByteLength = (new TextEncoder().encode(data.quote.text)).length;
                    const totalByteLength = textByteLength + quoteByteLength + quoteOverhead;
                    const maxLength = this.getMaxTextLength();
                    if (totalByteLength > maxLength) {
                        const newLength = Math.max(3, maxLength - textByteLength - quoteOverhead - 3);
                        const chunks = this.stringService.byteChunk(data.quote.text, newLength);
                        this.log.warn(`Quote too long, reducing length from ${quoteByteLength} to ≤${newLength} bytes`);
                        data.quote.text = chunks[0] + '…';
                    }
                }
                break;
            case 'file':
                subType = WebClientService.SUB_TYPE_FILE_MESSAGE;
                const fileData = data;
                if (this.chosenTask === "webrtc") {
                    const task = this.salty.getTask();
                    if (task.version === 'v0' && fileData.size > WebClientService.MAX_FILE_SIZE_WEBRTC_TASK_V0) {
                        throw this.$translate.instant('error.FILE_TOO_LARGE_WEB');
                    }
                }
                if (fileData.size > this.clientInfo.capabilities.maxFileSize) {
                    throw this.$translate.instant('error.FILE_TOO_LARGE', {
                        maxmb: Math.floor(this.clientInfo.capabilities.maxFileSize / 1024 / 1024),
                    });
                }
                reflectedType = 'file';
                if (fileData.sendAsFile !== true) {
                    const mimeType = fileData.fileType;
                    if (this.mimeService.isAudio(mimeType, this.clientInfo.os)) {
                        reflectedType = 'audio';
                    }
                    else if (this.mimeService.isImage(mimeType)) {
                        reflectedType = 'image';
                    }
                    else if (this.mimeService.isVideo(mimeType)) {
                        reflectedType = 'video';
                    }
                }
                break;
            default:
                this.log.error('Invalid message type:', sendType);
                throw this.$translate.instant('error.ERROR_OCCURRED');
        }
        if (this.messages.getList(receiver).length === 0) {
            await this.requestMessages(receiver);
        }
        const id = this.createRandomWireMessageId();
        let temporaryMessage;
        try {
            temporaryMessage = this.messageService.createTemporary(id, receiver, reflectedType, data, options.previewDataUrl);
        }
        catch (error) {
            this.log.error(error);
            throw this.$translate.instant('error.ERROR_OCCURRED');
        }
        this.messages.addNewer(receiver, [temporaryMessage]);
        const args = {
            [WebClientService.ARGUMENT_RECEIVER_TYPE]: receiver.type,
            [WebClientService.ARGUMENT_RECEIVER_ID]: receiver.id,
        };
        const sendPromise = this.sendCreateWireMessage(subType, true, args, data, id);
        sendPromise.catch((error) => {
            this.arpLog.error('Error sending message:', error);
            this.messages.removeTemporary(receiver, temporaryMessage.temporaryId);
            let errorMessage;
            switch (error) {
                case 'fileTooLarge':
                    errorMessage = this.$translate.instant('error.FILE_TOO_LARGE_GENERIC');
                    break;
                case 'valueTooLong':
                    errorMessage = this.$translate.instant('validationError.modifyReceiver.valueTooLong');
                    break;
                case 'blocked':
                    errorMessage = this.$translate.instant('error.CONTACT_BLOCKED');
                    break;
                default:
                    errorMessage = this.$translate.instant('error.ERROR_OCCURRED');
            }
            this.alerts.push({
                source: 'sendMessage',
                type: 'alert',
                message: errorMessage,
            });
        });
        if (options.waitUntilAcknowledged) {
            await sendPromise;
        }
    }
    ackMessage(receiver, message, acknowledged = true) {
        if (message === null || message === undefined) {
            return;
        }
        const args = {
            [WebClientService.ARGUMENT_RECEIVER_TYPE]: receiver.type,
            [WebClientService.ARGUMENT_RECEIVER_ID]: receiver.id,
            [WebClientService.ARGUMENT_MESSAGE_ID]: message.id.toString(),
            [WebClientService.ARGUMENT_MESSAGE_ACKNOWLEDGED]: acknowledged,
        };
        const subType = WebClientService.SUB_TYPE_ACK;
        this.sendRequestWireMessage(subType, !this.requiresTemporaryIdBackwardsCompatibility, args)
            .catch(this.logOnReject(WebClientService.TYPE_REQUEST, subType));
    }
    deleteMessage(receiver, message) {
        if (message === null || message === undefined) {
            return;
        }
        const args = {
            [WebClientService.ARGUMENT_RECEIVER_TYPE]: receiver.type,
            [WebClientService.ARGUMENT_RECEIVER_ID]: receiver.id,
            [WebClientService.ARGUMENT_MESSAGE_ID]: message.id.toString(),
        };
        const subType = WebClientService.SUB_TYPE_MESSAGE;
        this.sendDeleteWireMessage(subType, true, args)
            .catch(this.logOnReject(WebClientService.TYPE_DELETE, subType));
    }
    sendMeIsTyping(receiver, isTyping) {
        const args = { [WebClientService.ARGUMENT_RECEIVER_ID]: receiver.id };
        const data = { [WebClientService.ARGUMENT_IS_TYPING]: isTyping };
        this.sendUpdateWireMessage(WebClientService.SUB_TYPE_TYPING, false, args, data);
    }
    sendKeyPersisted() {
        const subType = WebClientService.SUB_TYPE_KEY_PERSISTED;
        this.sendRequestWireMessage(subType, !this.requiresTemporaryIdBackwardsCompatibility)
            .catch(this.logOnReject(WebClientService.TYPE_REQUEST, subType));
    }
    sendActiveConversation(conversation) {
        this.sendUpdateWireMessage(WebClientService.SUB_TYPE_ACTIVE_CONVERSATION, true, {
            [WebClientService.ARGUMENT_RECEIVER_TYPE]: conversation.type,
            [WebClientService.ARGUMENT_RECEIVER_ID]: conversation.id,
        }, undefined);
    }
    addContact(threemaId) {
        const data = {
            [WebClientService.ARGUMENT_IDENTITY]: threemaId,
        };
        const subType = WebClientService.SUB_TYPE_CONTACT;
        return this.sendCreateWireMessage(subType, true, undefined, data);
    }
    modifyContact(threemaId, firstName, lastName, avatar) {
        const data = {};
        if (firstName !== undefined) {
            data[WebClientService.ARGUMENT_FIRST_NAME] = firstName;
        }
        if (lastName !== undefined) {
            data[WebClientService.ARGUMENT_LAST_NAME] = lastName;
        }
        if (avatar !== undefined) {
            data[WebClientService.ARGUMENT_AVATAR] = avatar;
        }
        const contact = this.contacts.get(threemaId);
        if (Object.keys(data).length === 0) {
            this.arpLog.warn('Trying to modify contact without any changes');
            return Promise.resolve(contact);
        }
        const args = {
            [WebClientService.ARGUMENT_IDENTITY]: threemaId,
        };
        const subType = WebClientService.SUB_TYPE_CONTACT;
        const promise = this.sendUpdateWireMessage(subType, true, args, data);
        if (avatar !== undefined) {
            this.contacts.get(threemaId).avatar = {};
            this.requestAvatar(contact, false);
        }
        return promise;
    }
    modifyConversation(conversation, isPinned) {
        const DATA_STARRED = 'isStarred';
        const args = {
            [WebClientService.ARGUMENT_RECEIVER_TYPE]: conversation.type,
            [WebClientService.ARGUMENT_RECEIVER_ID]: conversation.id,
        };
        const data = {};
        if (hasValue(isPinned)) {
            data[DATA_STARRED] = isPinned;
        }
        if (Object.keys(data).length === 0) {
            this.arpLog.warn('Trying to modify conversation without any changes');
            return Promise.resolve(null);
        }
        const subType = WebClientService.SUB_TYPE_CONVERSATION;
        return this.sendUpdateWireMessage(subType, true, args, data);
    }
    createGroup(members, name = null, avatar) {
        const data = {
            [WebClientService.ARGUMENT_MEMBERS]: members,
            [WebClientService.ARGUMENT_NAME]: name,
        };
        if (hasValue(avatar)) {
            data[WebClientService.ARGUMENT_AVATAR] = avatar;
        }
        const subType = WebClientService.SUB_TYPE_GROUP;
        return this.sendCreateWireMessage(subType, true, undefined, data);
    }
    modifyGroup(id, members, name, avatar) {
        const data = {
            [WebClientService.ARGUMENT_MEMBERS]: members,
        };
        if (name !== undefined) {
            data[WebClientService.ARGUMENT_NAME] = name;
        }
        if (avatar !== undefined) {
            data[WebClientService.ARGUMENT_AVATAR] = avatar;
        }
        const args = {
            [WebClientService.ARGUMENT_RECEIVER_ID]: id,
        };
        const subType = WebClientService.SUB_TYPE_GROUP;
        const promise = this.sendUpdateWireMessage(subType, true, args, data);
        if (avatar !== undefined) {
            this.groups.get(id).avatar = {};
        }
        return promise;
    }
    leaveGroup(group) {
        if (group === null || group === undefined || !group.access.canLeave) {
            return Promise.reject('not allowed');
        }
        const args = {
            [WebClientService.ARGUMENT_RECEIVER_ID]: group.id,
            [WebClientService.ARGUMENT_DELETE_TYPE]: WebClientService.DELETE_GROUP_TYPE_LEAVE,
        };
        const subType = WebClientService.SUB_TYPE_GROUP;
        return this.sendDeleteWireMessage(subType, true, args);
    }
    deleteGroup(group) {
        if (group === null || group === undefined || !group.access.canDelete) {
            return Promise.reject('not allowed');
        }
        const args = {
            [WebClientService.ARGUMENT_RECEIVER_ID]: group.id,
            [WebClientService.ARGUMENT_DELETE_TYPE]: WebClientService.DELETE_GROUP_TYPE_DELETE,
        };
        const subType = WebClientService.SUB_TYPE_GROUP;
        return this.sendDeleteWireMessage(subType, true, args);
    }
    syncGroup(group) {
        if (group === null || group === undefined || !group.access.canSync) {
            return Promise.reject('not allowed');
        }
        const args = {
            [WebClientService.ARGUMENT_RECEIVER_ID]: group.id,
        };
        const subType = WebClientService.SUB_TYPE_GROUP_SYNC;
        return this.sendRequestWireMessage(subType, true, args);
    }
    createDistributionList(members, name = null) {
        const data = {
            [WebClientService.ARGUMENT_MEMBERS]: members,
            [WebClientService.ARGUMENT_NAME]: name,
        };
        const subType = WebClientService.SUB_TYPE_DISTRIBUTION_LIST;
        return this.sendCreateWireMessage(subType, true, undefined, data);
    }
    modifyDistributionList(id, members, name = null) {
        const args = {
            [WebClientService.ARGUMENT_RECEIVER_ID]: id,
        };
        const data = {
            [WebClientService.ARGUMENT_MEMBERS]: members,
            [WebClientService.ARGUMENT_NAME]: name,
        };
        const subType = WebClientService.SUB_TYPE_DISTRIBUTION_LIST;
        return this.sendUpdateWireMessage(subType, true, args, data);
    }
    deleteDistributionList(distributionList) {
        if (distributionList === null || distributionList === undefined || !distributionList.access.canDelete) {
            return Promise.reject('not allowed');
        }
        const args = {
            [WebClientService.ARGUMENT_RECEIVER_ID]: distributionList.id,
        };
        const subType = WebClientService.SUB_TYPE_DISTRIBUTION_LIST;
        return this.sendDeleteWireMessage(subType, true, args);
    }
    cleanReceiverConversation(receiver) {
        if (receiver === null || receiver === undefined) {
            return Promise.reject('invalidIdentity');
        }
        const args = {
            [WebClientService.ARGUMENT_RECEIVER_TYPE]: receiver.type,
            [WebClientService.ARGUMENT_RECEIVER_ID]: receiver.id,
        };
        const subType = WebClientService.SUB_TYPE_CLEAN_RECEIVER_CONVERSATION;
        return this.sendDeleteWireMessage(subType, true, args);
    }
    modifyProfile(nickname, avatar) {
        const data = {};
        if (nickname !== undefined && nickname !== null) {
            data[WebClientService.ARGUMENT_NICKNAME] = nickname;
        }
        if (avatar !== undefined) {
            data[WebClientService.ARGUMENT_AVATAR] = avatar;
        }
        if (Object.keys(data).length === 0) {
            this.arpLog.warn('Trying to modify profile without any changes');
            return Promise.resolve(null);
        }
        const subType = WebClientService.SUB_TYPE_PROFILE;
        return this.sendUpdateWireMessage(subType, true, undefined, data);
    }
    isTyping(contact) {
        return this.typing.isTyping(contact);
    }
    getQuote(receiver) {
        return this.drafts.getQuote(receiver);
    }
    setQuote(receiver, message) {
        this.drafts.removeQuote(receiver);
        if (message !== null) {
            const quoteText = this.messageService.getQuoteText(message);
            const hasMessageId = message.id != null && message.id.length > 0;
            if (hasValue(quoteText) || (this.appCapabilities.quotesV2 && hasMessageId)) {
                const quote = {
                    identity: message.isOutbox ? this.me.id : message.partnerId,
                    text: quoteText ?? this.$translate.instant(`messageTypes.${message.type}`),
                };
                if (hasMessageId) {
                    quote.messageId = message.id;
                }
                this.drafts.setQuote(receiver, quote);
                this.$rootScope.$broadcast('onQuoted', {
                    receiver: receiver,
                    quote: quote,
                });
            }
        }
    }
    setDraft(receiver, message = null) {
        if (message === null || message.trim().length === 0) {
            this.drafts.removeText(receiver);
        }
        else {
            this.drafts.setText(receiver, message.trim());
        }
    }
    getDraft(receiver) {
        return this.drafts.getText(receiver);
    }
    _resetInitializationSteps() {
        this.arpLog.debug('Reset initialization steps');
        this.initialized.clear();
        this.pendingInitializationStepRoutines = new Set();
    }
    _resetFields() {
        this._resetInitializationSteps();
        this.receivers = this.container.createReceivers();
        this.conversations = this.container.createConversations();
        this.messages = this.container.createMessages();
        this.typingInstance = this.container.createTyping();
        this.conversations.setConverter(this.container.Converter.addReceiverToConversation(this.receivers));
        this.conversations.setFilter(this.container.Filters.hasData(this.receivers));
    }
    _requestInitialData() {
        this.messages.clear(this.$rootScope);
        this.requestClientInfo();
        this.requestProfile();
        this.requestReceivers();
        this.requestConversations();
        this.requestBatteryStatus();
    }
    _receiveResponseConfirmAction(message) {
        this.arpLog.debug('Received confirmAction response');
        const future = this.popWireMessageFuture(message);
        if (!message.ack.success) {
            future.reject(message.ack.error);
        }
        else {
            future.resolve();
        }
    }
    _receiveResponseReceivers(message) {
        this.arpLog.debug('Received receivers response');
        const future = this.popWireMessageFuture(message, this.requiresTemporaryIdBackwardsCompatibility);
        if ((!this.requiresTemporaryIdBackwardsCompatibility && message.ack !== undefined) && !message.ack.success) {
            future.reject(message.ack.error);
        }
        const data = message.data;
        if (data === undefined) {
            this.arpLog.warn('Invalid receivers response, data missing');
            return future.reject('invalidResponse');
        }
        this.runAfterInitializationSteps([
            "profile",
        ], () => {
            this.sortContacts(data.contact);
            this.receivers.set(data);
            this.registerInitializationStep("receivers");
            future.resolve();
        });
    }
    _receiveResponseContactDetail(message) {
        this.arpLog.debug('Received contact detail response');
        const future = this.popWireMessageFuture(message);
        if (!message.ack.success) {
            return future.reject(message.ack.error);
        }
        const args = message.args;
        const data = message.data;
        if (args === undefined || data === undefined) {
            this.arpLog.error('Invalid contact response, args or data missing');
            return future.reject('invalidResponse');
        }
        const contactReceiver = this.receivers.contacts
            .get(args[WebClientService.ARGUMENT_IDENTITY]);
        const receiver = data[WebClientService.SUB_TYPE_RECEIVER];
        if (hasValue(receiver)) {
            contactReceiver.systemContact =
                receiver[WebClientService.ARGUMENT_SYSTEM_CONTACT];
        }
        future.resolve(contactReceiver);
    }
    _receiveUpdateAlert(message) {
        this.arpLog.debug('Received alert');
        this.alerts.push({
            source: message.args.source,
            type: message.args.type,
            message: message.data.message,
        });
    }
    _receiveRequestConnectionAck(message) {
        this._sendConnectionAck();
    }
    _receiveUpdateConnectionAck(message) {
        this.arpLog.debug('Received connection ack');
        if (!hasValue(message.data)) {
            this.arpLog.warn('Invalid connectionAck message: data missing');
            return;
        }
        if (!hasValue(message.data.sequenceNumber)) {
            this.arpLog.warn('Invalid connectionAck message: sequenceNumber missing');
            return;
        }
        const sequenceNumber = message.data.sequenceNumber;
        const size = this.currentChunkCache.byteLength;
        let result;
        this.arpLog.debug(`Pruning cache (local-sn=${this.currentChunkCache.sequenceNumber.get()}, ` +
            `remote-sn=${sequenceNumber})`);
        try {
            result = this.currentChunkCache.prune(sequenceNumber);
        }
        catch (error) {
            this.arpLog.error(error);
            this.failSession();
            return;
        }
        this.arpLog.debug(`Chunk cache pruned, acknowledged: ${result.acknowledged}, left: ${result.left}, size: ` +
            `${size} -> ${this.currentChunkCache.byteLength}`);
        if (this.pendingAckRequest !== null && sequenceNumber >= this.pendingAckRequest) {
            this.pendingAckRequest = null;
        }
    }
    _receiveUpdateConnectionDisconnect(message) {
        this.arpLog.debug('Received connectionDisconnect');
        if (!hasValue(message.data) || !hasValue(message.data.reason)) {
            this.arpLog.warn('Invalid connectionDisconnect message: data or reason missing');
            return;
        }
        const reason = message.data.reason;
        this.arpLog.debug(`Disconnecting requested (reason: ${reason})`);
        let alertMessage;
        switch (reason) {
            case "stop":
                alertMessage = 'connection.SESSION_STOPPED';
                break;
            case "delete":
                alertMessage = 'connection.SESSION_DELETED';
                break;
            case "disable":
                alertMessage = 'connection.WEBCLIENT_DISABLED';
                break;
            case "replace":
                alertMessage = 'connection.SESSION_REPLACED';
                break;
            case "oom":
                alertMessage = 'connection.OUT_OF_MEMORY';
                break;
            case "error":
                alertMessage = 'connection.SESSION_ERROR';
                break;
            default:
                alertMessage = 'connection.SESSION_ERROR';
                this.arpLog.error('Unknown disconnect reason:', reason);
                break;
        }
        const isWelcome = this.$state.includes('welcome');
        this.stop({
            reason: reason,
            send: false,
            close: 'welcome',
        });
        if (isWelcome) {
            this.$state.reload().catch((error) => {
                this.log.error('Unable to reload state:', error);
            });
        }
        this.showAlert(alertMessage);
    }
    _receiveConnectionInfo(message) {
        this.arpLog.debug('Received connectionInfo from device');
        if (!hasValue(message.data)) {
            this.connectionInfoFuture.reject('Invalid connectionInfo message: data missing');
            return;
        }
        if (!hasValue(message.data.id)) {
            this.connectionInfoFuture.reject('Invalid connectionInfo message: data.id is missing');
            return;
        }
        if (!(message.data.id instanceof ArrayBuffer)) {
            this.connectionInfoFuture.reject('Invalid connectionInfo message: data.id is of invalid type');
            return;
        }
        const resume = message.data.resume;
        if (resume !== undefined) {
            if (!hasValue(resume.id)) {
                this.connectionInfoFuture.reject('Invalid connectionInfo message: data.resume.id is missing');
                return;
            }
            if (!hasValue(resume.sequenceNumber)) {
                const error = 'Invalid connectionInfo message: data.resume.sequenceNumber is missing';
                this.connectionInfoFuture.reject(error);
                return;
            }
            if (!(resume.id instanceof ArrayBuffer)) {
                this.connectionInfoFuture.reject('Invalid connectionInfo message: data.resume.id is of invalid type');
                return;
            }
            if (resume.sequenceNumber < 0 || resume.sequenceNumber > WebClientService.SEQUENCE_NUMBER_MAX) {
                const error = 'Invalid connectionInfo message: data.resume.sequenceNumber is invalid';
                this.connectionInfoFuture.reject(error);
                return;
            }
        }
        this.connectionInfoFuture.resolve(message.data);
    }
    _receiveReplyReceiver(message, receiverType, future) {
        this.arpLog.debug(`Received ${receiverType} ${message.subType}`);
        if (message.ack !== undefined && !message.ack.success) {
            return future.reject(message.ack.error);
        }
        const data = message.data;
        if (data === undefined) {
            this.arpLog.error(`Invalid ${receiverType} response, 'data' is missing`);
            return future.reject('invalidResponse');
        }
        const receiver = data[WebClientService.SUB_TYPE_RECEIVER];
        if (receiver.type === undefined) {
            receiver.type = receiverType;
        }
        if (isContactReceiver(receiver)) {
            this.receivers.extendContact(receiver);
        }
        else if (isGroupReceiver(receiver)) {
            this.receivers.extendGroup(receiver);
        }
        else if (isDistributionListReceiver(receiver)) {
            this.receivers.extendDistributionList(receiver);
        }
        future.resolve(receiver);
    }
    _receiveCreateContact(message) {
        const future = this.popWireMessageFuture(message);
        this._receiveReplyReceiver(message, 'contact', future);
    }
    _receiveCreateGroup(message) {
        const future = this.popWireMessageFuture(message);
        this._receiveReplyReceiver(message, 'group', future);
    }
    _receiveCreateDistributionList(message) {
        const future = this.popWireMessageFuture(message);
        this._receiveReplyReceiver(message, 'distributionList', future);
    }
    _receiveCreateMessage(wireMessage) {
        this.arpLog.debug('Received create message response');
        const future = this.popWireMessageFuture(wireMessage);
        if (!wireMessage.ack.success) {
            return future.reject(wireMessage.ack.error);
        }
        const args = wireMessage.args;
        const data = wireMessage.data;
        if (args === undefined || data === undefined) {
            this.arpLog.warn('Invalid create message received, arguments or data missing');
            return future.reject('invalidResponse');
        }
        const receiverType = args[WebClientService.ARGUMENT_RECEIVER_TYPE];
        const receiverId = args[WebClientService.ARGUMENT_RECEIVER_ID];
        const messageId = data[WebClientService.ARGUMENT_MESSAGE_ID];
        if (receiverType === undefined || receiverId === undefined || messageId === undefined) {
            this.arpLog.warn("Invalid create received: 'type', 'id' or 'messageId' missing");
            return future.reject('invalidResponse');
        }
        const receiver = {
            type: receiverType,
            id: receiverId,
        };
        const message = this.messages.bindTemporaryToMessageId(receiver, wireMessage.ack.id, messageId);
        future.resolve(messageId);
        if (!this.messageService.isSentOrSendingFailed(message)) {
            const sentId = `${message.id}-sent`;
            this.wireMessageFutures.set(sentId, new Future());
            this.arpLogV.debug(`Added special wire message future: ${sentId}`);
        }
    }
    _receiveResponseConversations(message) {
        this.arpLog.debug('Received conversations response');
        const future = this.popWireMessageFuture(message, this.requiresTemporaryIdBackwardsCompatibility);
        if ((!this.requiresTemporaryIdBackwardsCompatibility && message.ack !== undefined) && !message.ack.success) {
            future.reject(message.ack.error);
        }
        const data = message.data;
        if (data === undefined) {
            this.arpLog.warn('Invalid conversation response, data missing');
            return future.reject('invalidResponse');
        }
        this.runAfterInitializationSteps([
            "receivers",
        ], () => {
            for (const conversation of data) {
                if (conversation.avatar !== undefined && conversation.avatar !== null) {
                    const receiver = this.receivers.getData({
                        id: conversation.id,
                        type: conversation.type,
                    });
                    if (receiver !== undefined && receiver.avatar === undefined) {
                        receiver.avatar = {
                            low: conversation.avatar,
                        };
                    }
                    delete conversation.avatar;
                }
            }
            this.conversations.set(data);
            this.updateUnreadCount();
            this.registerInitializationStep("conversations");
            future.resolve();
        });
    }
    _receiveResponseMessages(message) {
        this.arpLog.debug('Received messages response');
        const future = this.popWireMessageFuture(message, this.requiresTemporaryIdBackwardsCompatibility);
        if ((!this.requiresTemporaryIdBackwardsCompatibility && message.ack !== undefined) && !message.ack.success) {
            future.reject(message.ack.error);
        }
        const args = message.args;
        const data = message.data;
        if (args === undefined || data === undefined) {
            this.arpLog.warn('Invalid messages response, data or arguments missing');
            return future.reject('invalidResponse');
        }
        const type = args[WebClientService.ARGUMENT_RECEIVER_TYPE];
        const id = args[WebClientService.ARGUMENT_RECEIVER_ID];
        let more = args[WebClientService.ARGUMENT_HAS_MORE];
        if (type === undefined || id === undefined || more === undefined) {
            this.arpLog.warn('Invalid messages response, argument field missing');
            return future.reject('invalidResponse');
        }
        if (!isValidReceiverType(type)) {
            this.arpLog.warn('Invalid messages response, unknown receiver type (' + type + ')');
            return future.reject('invalidResponse');
        }
        if (this.userConfig.ARP_LOG_TRACE) {
            this.logChatMessages(message.type, message.subType, type, id, 'new', data);
        }
        const receiver = { type: type, id: id };
        if (data.length === 0) {
            more = false;
        }
        this.loadingMessages.delete(receiver.type + receiver.id);
        if (!this.messages.isRequested(receiver)) {
            this.arpLog.warn("Ignoring message response that hasn't been requested");
            return future.reject('invalidResponse');
        }
        this.messages.addOlder(receiver, data);
        this.messages.clearRequested(receiver);
        this.messages.setMore(receiver, more);
        this.messages.notify(receiver, this.$rootScope);
        future.resolve();
    }
    _receiveResponseAvatar(message) {
        this.arpLog.debug('Received avatar response');
        const future = this.popWireMessageFuture(message);
        if (!message.ack.success) {
            future.reject(message.ack.error);
        }
        const args = message.args;
        if (args === undefined) {
            this.arpLog.warn('Invalid message response: arguments missing');
            return future.reject('invalidResponse');
        }
        const avatar = message.data;
        if (avatar === undefined) {
            return future.resolve(null);
        }
        const type = args[WebClientService.ARGUMENT_RECEIVER_TYPE];
        const id = args[WebClientService.ARGUMENT_RECEIVER_ID];
        const highResolution = args[WebClientService.ARGUMENT_AVATAR_HIGH_RESOLUTION];
        if (type === undefined || id === undefined || highResolution === undefined) {
            this.arpLog.warn('Invalid avatar response, argument field missing');
            return future.reject('invalidResponse');
        }
        const field = highResolution ? 'high' : 'low';
        const receiverData = this.receivers.getData(args);
        if (!hasValue(receiverData.avatar)) {
            receiverData.avatar = {};
        }
        receiverData.avatar[field] = avatar;
        future.resolve(avatar);
    }
    _receiveResponseThumbnail(message) {
        this.arpLog.debug('Received thumbnail response');
        const future = this.popWireMessageFuture(message);
        if (!message.ack.success) {
            future.reject(message.ack.error);
        }
        const args = message.args;
        if (args === undefined) {
            this.arpLog.warn('Invalid message response: arguments missing');
            return future.reject('invalidResponse');
        }
        const thumbnail = message.data;
        if (thumbnail === undefined) {
            return future.resolve(null);
        }
        const type = args[WebClientService.ARGUMENT_RECEIVER_TYPE];
        const id = args[WebClientService.ARGUMENT_RECEIVER_ID];
        const messageId = args[WebClientService.ARGUMENT_MESSAGE_ID];
        if (type === undefined || id === undefined || messageId === undefined) {
            this.arpLog.warn('Invalid thumbnail response, argument field missing');
            return future.reject('invalidResponse');
        }
        this.messages.setThumbnail(this.receivers.getData(args), messageId, thumbnail);
        future.resolve(thumbnail);
    }
    _receiveResponseBlob(message) {
        this.arpLog.debug('Received blob response');
        const future = this.popWireMessageFuture(message);
        if (!message.ack.success) {
            return future.reject(message.ack.error);
        }
        const args = message.args;
        const data = message.data;
        if (args === undefined) {
            this.arpLog.warn('Invalid message response, args missing');
            return future.reject('invalidResponse');
        }
        const receiverType = args[WebClientService.ARGUMENT_RECEIVER_TYPE];
        const receiverId = args[WebClientService.ARGUMENT_RECEIVER_ID];
        const msgId = args[WebClientService.ARGUMENT_MESSAGE_ID];
        if (receiverType === undefined || receiverId === undefined || msgId === undefined) {
            this.arpLog.warn('Invalid blob response, argument field missing');
            return future.reject('invalidResponse');
        }
        const blobInfo = {
            buffer: data[WebClientService.DATA_FIELD_BLOB_BLOB],
            mimetype: data[WebClientService.DATA_FIELD_BLOB_TYPE],
            filename: data[WebClientService.DATA_FIELD_BLOB_NAME],
        };
        if (blobInfo.buffer === undefined || blobInfo.mimetype === undefined || blobInfo.filename === undefined) {
            this.arpLog.warn('Invalid blob response, data field missing');
            return future.reject('invalidResponse');
        }
        this.blobCache.set(msgId + receiverType, blobInfo);
        future.resolve(blobInfo);
    }
    _receiveUpdateConfirm(message) {
        this.arpLog.debug('Received wire message acknowledgement');
        const future = this.popWireMessageFuture(message);
        if (!message.ack.success) {
            future.reject(message.ack.error);
        }
        else {
            future.resolve();
        }
    }
    _receiveUpdateMessages(wireMessage) {
        this.arpLog.debug('Received messages update');
        const future = this.popWireMessageFuture(wireMessage, true);
        if (wireMessage.ack !== undefined && !wireMessage.ack.success) {
            return future.reject(wireMessage.ack.error);
        }
        const args = wireMessage.args;
        const data = wireMessage.data;
        if (args === undefined || data === undefined) {
            this.arpLog.warn('Invalid message update, data or arguments missing');
            return future.reject('invalidResponse');
        }
        const type = args[WebClientService.ARGUMENT_RECEIVER_TYPE];
        const id = args[WebClientService.ARGUMENT_RECEIVER_ID];
        const mode = args[WebClientService.ARGUMENT_MODE];
        if (type === undefined || id === undefined || mode === undefined) {
            this.arpLog.warn('Invalid message update, argument field missing');
            return future.reject('invalidResponse');
        }
        if (!isValidReceiverType(type)) {
            this.arpLog.warn('Invalid messages update, unknown receiver type (' + type + ')');
            return future.reject('invalidResponse');
        }
        if (this.userConfig.ARP_LOG_TRACE) {
            this.logChatMessages(wireMessage.type, wireMessage.subType, type, id, mode, data);
        }
        const receiver = { type: type, id: id };
        let notify = false;
        for (const message of data) {
            if (this.messageService.isSentOrSendingFailed(message)) {
                const sentId = `${message.id}-sent`;
                const sentFuture = this.wireMessageFutures.get(sentId);
                if (sentFuture !== undefined) {
                    this.wireMessageFutures.delete(sentId);
                    this.arpLogV.debug(`Removed special wire message future: ${sentId}`);
                    sentFuture.resolve();
                }
            }
            switch (mode) {
                case WebClientService.ARGUMENT_MODE_NEW:
                    if (!this.messages.update(receiver, message)) {
                        this.messages.addNewer(receiver, [message]);
                        this.typing.unsetTyping(receiver);
                    }
                    notify = true;
                    break;
                case WebClientService.ARGUMENT_MODE_MODIFIED:
                    if (!this.messages.update(receiver, message)) {
                        const log = `Received message update for unknown message (id ${message.id})`;
                        this.arpLog.error(log);
                        if (this.userConfig.ARP_LOG_TRACE) {
                            this.messages.addStatusMessage(receiver, 'Warning: ' + log);
                            notify = true;
                        }
                    }
                    break;
                case WebClientService.ARGUMENT_MODE_REMOVED:
                    if (!this.messages.remove(receiver, message.id)) {
                        this.arpLog.error(`Received message deletion for unknown message (id ${message.id})`);
                    }
                    notify = true;
                    break;
                default:
                    this.arpLog.warn('Invalid message response, unknown mode:', mode);
            }
        }
        if (notify) {
            this.messages.notify(receiver, this.$rootScope);
        }
        future.resolve();
    }
    _receiveUpdateReceiver(message) {
        this.arpLog.debug('Received receiver update');
        const args = message.args;
        const data = message.data;
        if (args === undefined || data === undefined) {
            this.arpLog.warn('Invalid receiver update, data or arguments missing');
            return;
        }
        const type = args[WebClientService.ARGUMENT_RECEIVER_TYPE];
        const id = args[WebClientService.ARGUMENT_RECEIVER_ID];
        const mode = args[WebClientService.ARGUMENT_MODE];
        if (type === undefined || mode === undefined || id === undefined) {
            this.arpLog.warn('Invalid receiver update, argument field missing');
            return;
        }
        switch (mode) {
            case WebClientService.ARGUMENT_MODE_NEW:
            case WebClientService.ARGUMENT_MODE_MODIFIED:
                const updatedReceiver = this.receivers.extend(type, data);
                if (updatedReceiver !== undefined && updatedReceiver.locked) {
                    this.messages.clearReceiverMessages(updatedReceiver);
                }
                break;
            case WebClientService.ARGUMENT_MODE_REMOVED:
                this.receivers.get(type).delete(id);
                break;
            default:
                this.arpLog.warn('Invalid receiver response, unknown mode:', mode);
        }
    }
    _receiveUpdateReceivers(message) {
        this.arpLog.debug('Received receivers update');
        const args = message.args;
        const data = message.data;
        if (args === undefined || data === undefined) {
            this.arpLog.warn('Invalid receiver update, data or arguments missing');
            return;
        }
        const type = args[WebClientService.ARGUMENT_RECEIVER_TYPE];
        if (type === undefined) {
            this.arpLog.warn('Invalid receivers update, argument field missing');
            return;
        }
        switch (type) {
            case 'contact':
                this.sortContacts(data);
                this.receivers.setContacts(data);
                break;
            case 'group':
                this.receivers.setGroups(data);
                break;
            case 'distributionList':
                this.receivers.setDistributionLists(data);
                break;
            default:
                this.arpLog.warn('Unknown receiver type:', type);
        }
    }
    _receiveUpdateTyping(message) {
        this.arpLog.debug('Received typing update');
        const args = message.args;
        const data = message.data;
        if (args === undefined || data === undefined) {
            this.arpLog.warn('Invalid typing update, data or arguments missing');
            return;
        }
        const identity = args[WebClientService.ARGUMENT_RECEIVER_ID];
        if (identity === undefined) {
            this.arpLog.warn('Invalid typing update, argument field missing');
            return;
        }
        const isTyping = data[WebClientService.ARGUMENT_IS_TYPING];
        if (isTyping === undefined) {
            this.arpLog.warn('Invalid typing update, data field missing');
            return;
        }
        const receiver = { id: identity, type: 'contact' };
        if (isTyping === true) {
            this.typing.setTyping(receiver);
        }
        else {
            this.typing.unsetTyping(receiver);
        }
    }
    _receiveUpdateConversation(message) {
        this.arpLog.debug('Received conversation update');
        const args = message.args;
        const data = message.data;
        if (args === undefined || data === undefined) {
            this.arpLog.warn('Invalid conversation update, data or arguments missing');
            return;
        }
        const receiver = this.receivers.getData({ type: data.type, id: data.id });
        if (!hasValue(receiver)) {
            const log = `Received conversation update for unknown ${data.type} receiver`;
            this.arpLog.error(log);
            return;
        }
        const type = args[WebClientService.ARGUMENT_MODE];
        switch (type) {
            case WebClientService.ARGUMENT_MODE_NEW:
                if (this.conversations.find(data) !== null) {
                    this.arpLog.error('Received update/conversation with mode=new for existing conversation');
                    break;
                }
                this.conversations.add(data);
                break;
            case WebClientService.ARGUMENT_MODE_MODIFIED:
                if (data.unreadCount > 0) {
                    const oldConversation = this.conversations.updateOrAdd(data, true);
                    if (oldConversation === null) {
                        this.onNewMessage(data.latestMessage, data, receiver);
                    }
                    else {
                        const unreadCountIncreased = data.unreadCount > oldConversation.unreadCount;
                        const unreadCountDecreased = data.unreadCount < oldConversation.unreadCount;
                        if (unreadCountIncreased) {
                            this.onNewMessage(data.latestMessage, data, receiver);
                        }
                        else if (unreadCountDecreased) {
                            this.notificationService.hideNotification(data.type + '-' + data.id);
                        }
                    }
                }
                else {
                    this.conversations.updateOrAdd(data, false);
                    this.notificationService.hideNotification(data.type + '-' + data.id);
                }
                break;
            case WebClientService.ARGUMENT_MODE_REMOVED:
                this.conversations.remove(data);
                this.messages.clearReceiverMessages(receiver);
                this.receiverListener.forEach((listener) => {
                    this.arpLog.debug('Call on removed listener');
                    listener.onConversationRemoved(receiver);
                });
                break;
            default:
                this.arpLog.warn('Received conversation without a mode');
                break;
        }
        this.updateUnreadCount();
    }
    _receiveUpdateAvatar(message) {
        this.arpLog.debug('Received avatar update');
        const args = message.args;
        const data = message.data;
        if (args === undefined) {
            this.arpLog.warn('Invalid avatar update, arguments missing');
            return;
        }
        const receiver = this.receivers.getData({ type: args.type, id: args.id });
        if (receiver === undefined) {
            this.arpLog.error('Received avatar update for nonexistent receiver');
            return;
        }
        receiver.avatar = {
            low: hasValue(data) ? data : undefined,
            high: undefined,
        };
    }
    _receiveUpdateBatteryStatus(message) {
        this.arpLog.debug('Received battery status');
        const future = this.popWireMessageFuture(message, true);
        if (message.ack !== undefined && !message.ack.success) {
            return future.reject(message.ack.error);
        }
        const data = message.data;
        if (data === undefined) {
            this.arpLog.warn('Invalid battery status message, data missing');
            return future.reject('invalidResponse');
        }
        this.batteryStatusService.setStatus(data);
        future.resolve();
    }
    _receiveUpdateContact(message) {
        const future = this.popWireMessageFuture(message);
        this._receiveReplyReceiver(message, 'contact', future);
    }
    _receiveUpdateGroup(message) {
        const future = this.popWireMessageFuture(message);
        this._receiveReplyReceiver(message, 'group', future);
    }
    _receiveUpdateDistributionList(message) {
        const future = this.popWireMessageFuture(message);
        this._receiveReplyReceiver(message, 'distributionList', future);
    }
    _receiveUpdateProfile(message) {
        this.arpLog.debug('Received profile update');
        const data = message.data;
        if (data === undefined) {
            this.arpLog.warn('Invalid profile update message, data missing');
            return;
        }
        if (hasValue(data.publicNickname)) {
            if (data.publicNickname === this.me.id) {
                this.me.publicNickname = undefined;
                this.me.displayName = this.$translate.instant('messenger.ME');
            }
            else {
                this.me.publicNickname = data.publicNickname;
                this.me.displayName = data.publicNickname;
            }
        }
        if (data.avatar !== undefined) {
            if (data.avatar === null) {
                this.me.avatar = {};
            }
            else {
                this.me.avatar = { high: data.avatar };
            }
            void this.requestAvatar(this.me, false);
        }
    }
    _receiveResponseClientInfo(message) {
        this.arpLog.debug('Received client info response');
        const future = this.popWireMessageFuture(message, this.requiresTemporaryIdBackwardsCompatibility);
        if ((!this.requiresTemporaryIdBackwardsCompatibility && message.ack !== undefined) && !message.ack.success) {
            future.reject(message.ack.error);
        }
        const data = message.data;
        if (data === undefined) {
            this.arpLog.warn('Invalid client info, data field missing');
            return future.reject('invalidResponse');
        }
        function getOrDefault(field, defaultVal) {
            if (field === undefined) {
                return defaultVal;
            }
            return field;
        }
        this.clientInfo = {
            device: data.device,
            os: data.os,
            osVersion: data.osVersion,
            isWork: hasValue(data.isWork) ? data.isWork : false,
            pushToken: data.pushToken,
            configuration: {
                voipEnabled: getOrDefault(data.configuration.voipEnabled, true),
                voipForceTurn: getOrDefault(data.configuration.voipForceTurn, false),
                largeSingleEmoji: getOrDefault(data.configuration.largeSingleEmoji, true),
                showInactiveIDs: getOrDefault(data.configuration.showInactiveIDs, true),
            },
            capabilities: {
                maxGroupSize: getOrDefault(data.capabilities.maxGroupSize, 50),
                maxFileSize: getOrDefault(data.capabilities.maxFileSize, 50 * 1024 * 1024),
                maxMessageBodySize: getOrDefault(data.capabilities.maxMessageBodySize, 3500),
                distributionLists: getOrDefault(data.capabilities.distributionLists, true),
                quotesV2: getOrDefault(data.capabilities.quotesV2, false),
                imageFormat: data.capabilities.imageFormat,
                groupReactions: getOrDefault(data.capabilities.groupReactions, false),
                emojiReactions: getOrDefault(data.capabilities.emojiReactions, false),
                mdm: data.capabilities.mdm,
            },
        };
        this.arpLog.debug('Client device:', this.clientInfo.device);
        this.arpLog.debug('Client capabilities:', this.clientInfo.capabilities);
        try {
            localStorage.setItem('lastConnectedClientOS', this.clientInfo.os);
        }
        catch (error) {
            this.log.error(`Could not write to localSotrage: ${error}`);
        }
        try {
            localStorage.setItem('lastConnectedAppFlavor', this.clientInfo.isWork ? 'work' : 'consumer');
        }
        catch (error) {
            this.log.error(`Could not write to localSotrage: ${error}`);
        }
        if (this.clientInfo.pushToken) {
            this.pushToken = this.clientInfo.pushToken;
            switch (this.clientInfo.os) {
                case "android":
                    const hmsPrefix = `${"hms"};`;
                    const threemaGatewayPrefix = `${"threema-gateway"};`;
                    if (this.pushToken.startsWith(hmsPrefix)) {
                        this.pushToken = this.pushToken.substring(hmsPrefix.length);
                        this.pushTokenType = "hms";
                    }
                    else if (this.pushToken.startsWith(threemaGatewayPrefix)) {
                        this.pushToken = this.pushToken.substring(threemaGatewayPrefix.length);
                        this.pushTokenType = "threema-gateway";
                    }
                    else {
                        this.pushTokenType = "gcm";
                    }
                    break;
                case "ios":
                    this.pushTokenType = "apns";
                    break;
                default:
                    this.arpLog.error('Invalid operating system in client info');
            }
        }
        if (this.pushToken !== null && this.pushTokenType !== null) {
            this.pushService.init(this.pushToken, this.pushTokenType);
        }
        if (this.clientInfo.isWork) {
            this.themeService.changeTheme("threemawork");
        }
        this.registerInitializationStep("client info");
        future.resolve();
    }
    _receiveResponseProfile(message) {
        this.arpLog.debug('Received profile response');
        const future = this.popWireMessageFuture(message, this.requiresTemporaryIdBackwardsCompatibility);
        if ((!this.requiresTemporaryIdBackwardsCompatibility && message.ack !== undefined) && !message.ack.success) {
            future.reject(message.ack.error);
        }
        const data = message.data;
        if (data === undefined) {
            this.arpLog.warn('Invalid client info, data field missing');
            return future.reject('invalidResponse');
        }
        const hasPublicNickname = hasValue(data.publicNickname) && data.publicNickname !== data.identity;
        this.receivers.setMe({
            type: 'me',
            id: data.identity,
            publicNickname: data.publicNickname,
            displayName: hasPublicNickname ? data.publicNickname : this.$translate.instant('messenger.ME'),
            publicKey: data.publicKey,
            avatar: {
                high: data.avatar,
            },
            featureMask: 0xFF,
            verificationLevel: 3,
            state: 'ACTIVE',
            locked: false,
            visible: true,
            hidden: false,
            access: {
                canChangeAvatar: true,
                canChangeFirstName: true,
                canChangeLastName: true,
            },
            color: '#000000',
        });
        this.registerInitializationStep("profile");
        future.resolve();
    }
    async setPassword(password, isAutoSession) {
        if (await this._maybeTrustKeys(password, isAutoSession)) {
            this.sendKeyPersisted();
        }
    }
    clearCache() {
        this._resetFields();
        this.blobCache.clear();
    }
    getMaxTextLength() {
        return this.clientInfo.capabilities.maxMessageBodySize;
    }
    getMaxGroupMemberSize() {
        return this.clientInfo.capabilities.maxGroupSize;
    }
    shouldNotify(settings, message) {
        if (settings.dnd.enabled) {
            if (settings.dnd.mentionOnly) {
                let textToSearch = '';
                if (message.type === 'text') {
                    textToSearch = message.body;
                }
                else if (message.caption) {
                    textToSearch = message.caption;
                }
                let quotedMe = false;
                if (message.quote) {
                    textToSearch += ' ' + message.quote.text;
                    quotedMe = message.quote.identity === this.me.id;
                }
                const forMe = textToSearch.indexOf('@[' + this.me.id + ']') !== -1;
                const forAll = textToSearch.indexOf('@[@@@@@@@@]') !== -1;
                return forMe || forAll || quotedMe;
            }
            else {
                return false;
            }
        }
        else {
            return true;
        }
    }
    onNewMessage(message, conversation, sender) {
        if (document.hasFocus()
            && this.receiverService.compare(conversation, this.receiverService.getActive())) {
            return;
        }
        if (sender.locked === true) {
            return;
        }
        const simplifiedNotification = this.notificationService.getAppNotificationSettings(conversation);
        if (!this.shouldNotify(simplifiedNotification, message)) {
            return;
        }
        let senderName = sender.id;
        if (sender.displayName) {
            senderName = sender.displayName;
        }
        else if (isContactReceiver(sender)) {
            senderName = '~' + sender.publicNickname;
        }
        const partner = this.receivers.getData({
            id: message.partnerId,
            type: 'contact',
        });
        const partnerName = partner.displayName || ('~' + partner.publicNickname);
        this.$translate('messenger.MESSAGE_NOTIFICATION_SUBJECT', { messageCount: conversation.unreadCount })
            .then((titlePrefix) => {
            const title = `${titlePrefix} ${senderName}`;
            let body = '';
            const messageType = message.type;
            const caption = message.caption;
            let captionString = '';
            if (caption !== undefined) {
                captionString = captionString + ': ' + caption;
            }
            const messageTypeString = this.$translate.instant('messageTypes.' + messageType);
            switch (messageType) {
                case 'text':
                    body = message.body;
                    break;
                case 'location':
                    body = messageTypeString + ': ' + message.location.description;
                    break;
                case 'file':
                    if (message.file.type === 'image/gif') {
                        body = this.$translate.instant('messageTypes.' + 'gif') + captionString;
                        break;
                    }
                    if (captionString.length > 0) {
                        body = messageTypeString + captionString;
                    }
                    else {
                        body = messageTypeString + ': ' + message.file.name;
                    }
                    break;
                case 'ballot':
                    body = messageTypeString;
                    break;
                case 'voipStatus':
                    let translationKey;
                    switch (message.voip.status) {
                        case 1:
                            translationKey = 'CALL_MISSED';
                            break;
                        case 2:
                            translationKey = message.isOutbox ? 'CALL_FINISHED_IN' : 'CALL_FINISHED_OUT';
                            break;
                        case 3:
                            translationKey = 'CALL_REJECTED';
                            break;
                        case 4:
                            translationKey = 'CALL_ABORTED';
                            break;
                        default:
                    }
                    if (translationKey !== undefined) {
                        body = this.$translate.instant('voip.' + translationKey);
                    }
                    break;
                default:
                    body = messageTypeString + captionString;
            }
            if (conversation.type === 'group') {
                body = partnerName + ': ' + body;
            }
            const tag = conversation.type + '-' + conversation.id;
            const avatar = (sender.avatar && sender.avatar.low)
                ? bufferToUrl(sender.avatar.low, 'image/png', this.arpLog)
                : null;
            this.notificationService.showNotification(tag, title, body, avatar, () => {
                this.$state.go('messenger.home.conversation', {
                    type: conversation.type,
                    id: conversation.id,
                    initParams: null,
                });
            }, undefined, undefined, simplifiedNotification.sound.muted);
        });
    }
    async _maybeTrustKeys(password, isAutoSession) {
        if (hasValue(password) && password.length > 0) {
            await this.trustedKeyStore.storeTrustedKey(this.salty.keyStore.secretKeyBytes, this.salty.peerPermanentKeyBytes, this.pushToken, this.pushTokenType, password, isAutoSession);
            this.log.info('Stored trusted key');
            return true;
        }
        return false;
    }
    updatePushToken(token, tokenType) {
        this.pushToken = token;
        this.pushTokenType = tokenType;
    }
    sendRequestWireMessage(subType, retransmit, args, data, id) {
        return this.sendWireMessage(WebClientService.TYPE_REQUEST, subType, retransmit, args, data, id);
    }
    sendUpdateWireMessage(subType, retransmit, args, data, id) {
        return this.sendWireMessage(WebClientService.TYPE_UPDATE, subType, retransmit, args, data, id);
    }
    sendCreateWireMessage(subType, retransmit, args, data, id) {
        return this.sendWireMessage(WebClientService.TYPE_CREATE, subType, retransmit, args, data, id);
    }
    sendDeleteWireMessage(subType, retransmit, args, data, id) {
        return this.sendWireMessage(WebClientService.TYPE_DELETE, subType, retransmit, args, data, id);
    }
    createRandomWireMessageId() {
        let id;
        do {
            id = randomString(6);
        } while (this.wireMessageFutures.has(id));
        return id;
    }
    sendWireMessage(type, subType, retransmit, args, data, id) {
        const message = {
            type: type,
            subType: subType,
        };
        let promise;
        if (retransmit) {
            if (id === undefined) {
                id = this.createRandomWireMessageId();
            }
            else if (this.wireMessageFutures.has(id)) {
                throw new Error('Duplicate id for wire message detected');
            }
            message.id = id;
            if (args === undefined) {
                args = {};
            }
            args[WebClientService.ARGUMENT_TEMPORARY_ID] = message.id;
            const future = new Future();
            this.arpLogV.debug(`Added wire message future: ${id} -> ${type}/${subType}`);
            this.wireMessageFutures.set(message.id, future);
            promise = future;
        }
        else {
            promise = Promise.resolve({
                id: '',
                success: true,
            });
        }
        if (args !== undefined) {
            message.args = args;
        }
        if (data !== undefined) {
            message.data = data;
        }
        this.send(message, retransmit);
        return promise;
    }
    static validateWireMessageAcknowledgement(ack) {
        if (!hasValue(ack.id)) {
            throw new Error("Invalid wire message acknowledgement: 'id' is missing");
        }
        if (!hasValue(ack.success)) {
            throw new Error("Invalid wire message acknowledgement: 'success' is missing");
        }
        switch (ack.success) {
            case true:
                break;
            case false:
                if (!hasValue(ack.error)) {
                    throw new Error("Invalid wire message acknowledgement: 'error' is missing");
                }
                break;
            default:
                throw new Error("Invalid wire message acknowledgement: 'success' is not a boolean");
        }
    }
    popWireMessageFuture(message, optional = false) {
        if (message.ack === undefined &&
            message.args !== undefined &&
            message.args[WebClientService.ARGUMENT_TEMPORARY_ID] !== undefined) {
            if (message.args[WebClientService.ARGUMENT_SUCCESS] === undefined) {
                message.args[WebClientService.ARGUMENT_SUCCESS] = true;
            }
            message.ack = {
                id: message.args[WebClientService.ARGUMENT_TEMPORARY_ID],
                success: message.args[WebClientService.ARGUMENT_SUCCESS],
                error: message.args[WebClientService.ARGUMENT_ERROR],
            };
        }
        let error;
        if (hasValue(message.ack)) {
            try {
                WebClientService.validateWireMessageAcknowledgement(message.ack);
            }
            catch (e) {
                error = e;
            }
        }
        else if (!optional) {
            throw new Error('Wire message did not contain an acknowledgement');
        }
        else {
            return new Future();
        }
        const id = message.ack.id;
        const future = this.wireMessageFutures.get(id);
        if (future !== undefined) {
            this.wireMessageFutures.delete(id);
            this.arpLogV.debug(`Removed wire message future: ${id} -> ` +
                `${message.type}/${message.subType}`);
        }
        else if (error === undefined) {
            error = new Error(`Wire message future not found for id: ${id}`);
        }
        if (error !== undefined) {
            if (future !== undefined) {
                future.reject('invalidResponse');
            }
            throw error;
        }
        return future;
    }
    _receiveRequest(type, message) {
        switch (type) {
            case WebClientService.SUB_TYPE_CONNECTION_ACK:
                this._receiveRequestConnectionAck(message);
                break;
            default:
                this.arpLog.warn(`Ignored request/${type}`);
                break;
        }
    }
    _receiveResponse(type, message) {
        switch (type) {
            case WebClientService.SUB_TYPE_CONFIRM_ACTION:
                this._receiveResponseConfirmAction(message);
                break;
            case WebClientService.SUB_TYPE_RECEIVERS:
                this._receiveResponseReceivers(message);
                break;
            case WebClientService.SUB_TYPE_CONVERSATIONS:
                this._receiveResponseConversations(message);
                break;
            case WebClientService.SUB_TYPE_MESSAGES:
                this._receiveResponseMessages(message);
                break;
            case WebClientService.SUB_TYPE_AVATAR:
                this._receiveResponseAvatar(message);
                break;
            case WebClientService.SUB_TYPE_THUMBNAIL:
                this._receiveResponseThumbnail(message);
                break;
            case WebClientService.SUB_TYPE_BLOB:
                this._receiveResponseBlob(message);
                break;
            case WebClientService.SUB_TYPE_CLIENT_INFO:
                this._receiveResponseClientInfo(message);
                break;
            case WebClientService.SUB_TYPE_PROFILE:
                this._receiveResponseProfile(message);
                break;
            case WebClientService.SUB_TYPE_CONTACT_DETAIL:
                this._receiveResponseContactDetail(message);
                break;
            default:
                this.arpLog.warn(`Ignored response/${type}`);
                break;
        }
    }
    _receiveUpdate(type, message) {
        switch (type) {
            case WebClientService.SUB_TYPE_CONFIRM:
                this._receiveUpdateConfirm(message);
                break;
            case WebClientService.SUB_TYPE_RECEIVER:
                this._receiveUpdateReceiver(message);
                break;
            case WebClientService.SUB_TYPE_RECEIVERS:
                this._receiveUpdateReceivers(message);
                break;
            case WebClientService.SUB_TYPE_MESSAGES:
                this._receiveUpdateMessages(message);
                break;
            case WebClientService.SUB_TYPE_TYPING:
                this._receiveUpdateTyping(message);
                break;
            case WebClientService.SUB_TYPE_CONVERSATION:
                this._receiveUpdateConversation(message);
                break;
            case WebClientService.SUB_TYPE_AVATAR:
                this._receiveUpdateAvatar(message);
                break;
            case WebClientService.SUB_TYPE_BATTERY_STATUS:
                this._receiveUpdateBatteryStatus(message);
                break;
            case WebClientService.SUB_TYPE_CONTACT:
                this._receiveUpdateContact(message);
                break;
            case WebClientService.SUB_TYPE_GROUP:
                this._receiveUpdateGroup(message);
                break;
            case WebClientService.SUB_TYPE_DISTRIBUTION_LIST:
                this._receiveUpdateDistributionList(message);
                break;
            case WebClientService.SUB_TYPE_PROFILE:
                this._receiveUpdateProfile(message);
                break;
            case WebClientService.SUB_TYPE_ALERT:
                this._receiveUpdateAlert(message);
                break;
            case WebClientService.SUB_TYPE_CONNECTION_ACK:
                this._receiveUpdateConnectionAck(message);
                break;
            case WebClientService.SUB_TYPE_CONNECTION_DISCONNECT:
                this._receiveUpdateConnectionDisconnect(message);
                break;
            default:
                this.arpLog.warn(`Ignored update/${type}`);
                break;
        }
    }
    _receiveCreate(type, message) {
        switch (type) {
            case WebClientService.SUB_TYPE_CONTACT:
                this._receiveCreateContact(message);
                break;
            case WebClientService.SUB_TYPE_GROUP:
                this._receiveCreateGroup(message);
                break;
            case WebClientService.SUB_TYPE_DISTRIBUTION_LIST:
                this._receiveCreateDistributionList(message);
                break;
            case WebClientService.SUB_TYPE_TEXT_MESSAGE:
            case WebClientService.SUB_TYPE_FILE_MESSAGE:
                this._receiveCreateMessage(message);
                break;
            default:
                this.arpLog.warn(`Ignored response/${type}`);
                break;
        }
    }
    msgpackEncode(data) {
        return msgpack.encode(data, this.msgpackEncoderOptions);
    }
    msgpackDecode(bytes) {
        return msgpack.decode(bytes, this.msgpackDecoderOptions);
    }
    send(message, retransmit) {
        this.arpLog.debug('Sending', message.type + '/' + message.subType, 'message');
        if (this.userConfig.ARP_LOG_TRACE) {
            this.arpLogV.debug('Outgoing:', message.type, '/', message.subType, new ConfidentialWireMessage(message));
        }
        switch (this.chosenTask) {
            case "webrtc":
                {
                    const bytes = this.msgpackEncode(message);
                    if (this.userConfig.MSGPACK_LOG_TRACE) {
                        this.msgpackLog.debug('Outgoing message payload: ' + msgpackVisualizer(bytes));
                    }
                    const box = this.secureDataChannelCrypto.encrypt(bytes);
                    const chunker = new chunkedDc.UnreliableUnorderedChunker(this.secureDataChannelMessageId++, box.toUint8Array(), this.secureDataChannelChunkLength);
                    for (const chunk of chunker) {
                        this.arpLogV.debug(`Data channel ${this.secureDataChannel.dc.label} outgoing ` +
                            `chunk of length ${chunk.byteLength}`);
                        this.secureDataChannel.write(chunk);
                    }
                }
                break;
            case "relayed-data":
                {
                    const canQueue = message.subType !== WebClientService.SUB_TYPE_CONNECTION_INFO;
                    const bytes = this.msgpackEncode(message);
                    if (this.userConfig.MSGPACK_LOG_TRACE) {
                        this.msgpackLog.debug('Outgoing message payload: ' + msgpackVisualizer(bytes));
                    }
                    const messageSequenceNumber = this.outgoingMessageSequenceNumber.increment();
                    const chunker = new chunkedDc.UnreliableUnorderedChunker(messageSequenceNumber, bytes, WebClientService.RELAYED_DATA_CHUNK_SIZE);
                    for (const chunk of chunker) {
                        this.sendChunk(chunk, retransmit, canQueue, true);
                    }
                    if (this.pendingAckRequest === null &&
                        this.currentChunkCache.byteLength > WebClientService.CHUNK_CACHE_SIZE_MAX) {
                        this.pendingAckRequest = this.currentChunkCache.sequenceNumber.get();
                        this._requestConnectionAck();
                    }
                }
                break;
            default:
                this.arpLog.error('Trying to send message, but no chosen task set');
        }
    }
    sendChunk(chunk, retransmit, canQueue, cache) {
        if (this.chosenTask !== "relayed-data") {
            throw new Error(`Cannot send chunk, not supported by task: ${this.chosenTask}`);
        }
        const shouldQueue = canQueue && this.previousChunkCache !== null;
        let chunkCache;
        if (shouldQueue) {
            chunkCache = this.previousChunkCache;
            this.arpLog.debug('Currently not connected, queueing chunk');
            if (!this.pushService.isAvailable()) {
                this.log.warn('Push service not available, cannot wake up peer!');
                retransmit = false;
            }
            if (retransmit) {
                this.sendPush();
            }
        }
        else {
            chunkCache = this.currentChunkCache;
        }
        if (cache) {
            this.arpLogV.debug(`Caching chunk (retransmit/push=${retransmit}:`, chunk);
            try {
                chunkCache.append(retransmit ? chunk : null);
            }
            catch (error) {
                this.arpLog.error(error);
                this.failSession();
                return;
            }
        }
        if (!shouldQueue) {
            this.arpLogV.debug(`Sending chunk (retransmit/push=${retransmit}:`, chunk);
            this.relayedDataTask.sendMessage(chunk.buffer);
            if (retransmit && this.startupDone) {
                this.schedulePush();
            }
        }
    }
    receiveChunk(chunk) {
        this.arpLogV.debug('Received chunk:', chunk);
        try {
            this.currentIncomingChunkSequenceNumber.increment();
        }
        catch (error) {
            this.arpLog.error(`Unable to continue session: ${error}`);
            this.failSession();
            return;
        }
        this.scheduleConnectionAck();
        this.cancelPush();
        this.unchunker.add(chunk);
    }
    handleIncomingMessageBytes(bytes) {
        this.arpLog.debug('New incoming message (' + bytes.byteLength + ' bytes)');
        if (this.userConfig.MSGPACK_LOG_TRACE) {
            this.msgpackLog.debug('Incoming message payload: ' + msgpackVisualizer(bytes));
        }
        const message = this.msgpackDecode(bytes);
        return this.handleIncomingMessage(message);
    }
    handleIncomingMessage(message) {
        this.arpLog.debug(`Received ${message.type}/${message.subType} message`);
        if (message.type === undefined) {
            this.arpLog.warn('Ignoring invalid message (no type attribute)');
            return;
        }
        else if (message.subType === undefined) {
            this.arpLog.warn('Ignoring invalid message (no subType attribute)');
            return;
        }
        if (this.userConfig.ARP_LOG_TRACE) {
            this.arpLogV.debug(`Incoming: ${message.type}/${message.subType}`, new ConfidentialWireMessage(copyDeepOrReference(message)));
        }
        this.$rootScope.$apply(() => {
            this.receive(message);
        });
    }
    receive(message) {
        if (!this.connectionInfoFuture.done) {
            if (message.type !== WebClientService.TYPE_UPDATE ||
                message.subType !== WebClientService.SUB_TYPE_CONNECTION_INFO) {
                this.arpLog.error('Unexpected message before handshake has been completed');
                this.failSession();
                return;
            }
            this._receiveConnectionInfo(message);
            return;
        }
        let messageHandler;
        switch (message.type) {
            case WebClientService.TYPE_REQUEST:
                messageHandler = this._receiveRequest;
                break;
            case WebClientService.TYPE_RESPONSE:
                messageHandler = this._receiveResponse;
                break;
            case WebClientService.TYPE_CREATE:
                messageHandler = this._receiveCreate;
                break;
            case WebClientService.TYPE_UPDATE:
                messageHandler = this._receiveUpdate;
                break;
            default:
                this.arpLog.warn(`Ignored message ${message.type}/${message.subType}`);
                break;
        }
        if (messageHandler !== undefined) {
            try {
                messageHandler.apply(this, [message.subType, message]);
            }
            catch (error) {
                this.arpLog.error('Unable to handle incoming wire message:', error);
                console.trace(error);
                return;
            }
        }
        let future;
        try {
            future = this.popWireMessageFuture(message);
        }
        catch {
        }
        if (future !== undefined) {
            this.arpLog.warn(`Unhandled message acknowledgement for type ${message.type}:`, message.ack);
            future.reject('unhandled');
        }
    }
    runAfterInitializationSteps(requiredSteps, callback) {
        for (const requiredStep of requiredSteps) {
            if (!this.initialized.has(requiredStep)) {
                this.arpLog.debug('Required initialization step', requiredStep, 'not completed, add pending routine');
                this.pendingInitializationStepRoutines.add({
                    requiredSteps: requiredSteps,
                    callback: callback,
                });
                return;
            }
        }
        callback.apply(this);
    }
    updateUnreadCount() {
        const totalUnreadCount = this.conversations
            .get()
            .reduce((a, b) => a + b.unreadCount, 0);
        this.stateService.unreadCount = totalUnreadCount;
    }
    get appConfig() {
        return this.clientInfo.configuration;
    }
    get appCapabilities() {
        return this.clientInfo.capabilities;
    }
    sortContacts(contacts) {
        const getSortableName = (name) => name.startsWith('~') ? name.substr(1) : name;
        let options;
        if (this.browserService.supportsExtendedLocaleCompare()) {
            options = {
                usage: 'sort',
                sensitivity: 'variant',
            };
        }
        const compareFunc = (a, b) => {
            if (a.id.startsWith('*') && !b.id.startsWith('*')) {
                return 1;
            }
            if (!a.id.startsWith('*') && b.id.startsWith('*')) {
                return -1;
            }
            const left = getSortableName(a.displayName);
            const right = getSortableName(b.displayName);
            return left.localeCompare(right, undefined, options);
        };
        contacts.sort(compareFunc);
    }
    clearIsTypingFlags() {
        this.typing.clearAll();
    }
    handleGlobalConnectionStateChange(stateChange) {
        const isOk = stateChange.state === "ok";
        const wasOk = stateChange.prevState === "ok";
        if (!isOk && wasOk && this.batteryStatusService.dataAvailable) {
            this.batteryStatusTimeout = this.timeoutService.register(() => {
                this.batteryStatusService.clearStatus();
                this.batteryStatusTimeout = null;
            }, 60000, true, 'batteryStatusHide');
        }
        else if (isOk && this.batteryStatusTimeout !== null) {
            this.timeoutService.cancel(this.batteryStatusTimeout);
            this.batteryStatusTimeout = null;
        }
    }
    logChatMessages(type, subType, receiverType, receiver, mode, messages) {
        for (const message of messages) {
            let id = message.id;
            if (this.clientInfo.os === "ios") {
                try {
                    id = u8aToHex(base64ToU8a(message.id));
                }
                catch { }
            }
            this.arpLogV.debug('Chat message:', `${type}/${subType}: receiver=${receiverType}/${receiver}, ` +
                `mode=${mode}, direction=${message.isOutbox ? 'out' : 'in'}, id=${id}, type=${message.type}, ` +
                `state=${message.state !== undefined ? message.state : '?'}, is-status=${message.isStatus}, ` +
                `date=${message.date}`);
        }
    }
    hasAppleNonVoipPushToken() {
        return this.pushTokenType === "apns"
            && this.pushToken !== null
            && !this.pushToken.endsWith('.voip');
    }
}
